/* ═══════════════════════════════════════════════════════════════
   custom-cartier.js — Header layout refinements
   Moves the search icon to the left (next to the hamburger menu)
   and inserts a language-toggle icon in place of the search.
   Preserves all existing functionality.
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // round43: v40 shim removed — custom-cartier.css is the single source of truth
  // round45: simplified locale toggle via direct navigation + link interceptor

  /* ─── Ensure <html dir="rtl"> when lang is Arabic ─── */
  function ensureRtlDir() {
    try {
      const lang = (document.documentElement.lang || '').toLowerCase();
      if (lang.startsWith('ar')) {
        if (document.documentElement.dir !== 'rtl') {
          document.documentElement.dir = 'rtl';
        }
      } else {
        if (document.documentElement.dir === 'rtl') {
          document.documentElement.dir = 'ltr';
        }
      }
    } catch (e) {}
  }
  ensureRtlDir();

  /* ─── Link click interceptor: force /ar prefix when user is in Arabic ─── */
  /* This guarantees that even if any link somehow lacks /ar, clicking it
     will stay in Arabic. Belt-and-suspenders for locale persistence. */
  function installLinkInterceptor() {
    document.addEventListener('click', function (e) {
      const lang = (document.documentElement.lang || '').toLowerCase();
      if (!lang.startsWith('ar')) return;
      // Find anchor ancestor
      let a = e.target;
      while (a && a.tagName !== 'A') a = a.parentElement;
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href) return;
      // Skip anchors, mailto, tel, javascript, external
      if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
      // Skip already-prefixed, absolute-external, and non-same-origin
      let url;
      try { url = new URL(href, location.origin); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      const p = url.pathname;
      // Skip admin / cdn / apps / checkouts
      if (/^\/(cdn|admin|apps|account|checkouts|localization|cart\/add|cart\/change|cart\/update|recommendations)\b/.test(p)) return;
      // Already has /ar prefix — fine
      if (p === '/ar' || p.startsWith('/ar/')) return;
      // Has a different locale prefix — skip (user is choosing english)
      if (/^\/(en|en-[a-z]{2})(\/|$)/i.test(p)) return;
      // Rewrite to prepend /ar
      const newPath = (p === '/' ? '/ar' : '/ar' + p) + url.search + url.hash;
      a.setAttribute('href', newPath);
    }, true);
  }
  installLinkInterceptor();

  function init() {
    const header = document.querySelector('header.header');
    if (!header) return;

    const iconsContainer = header.querySelector('.header__icons');
    if (!iconsContainer) return;

    /* ─── 1) Move the search icon to the LEFT, next to the menu ─── */
    /* Order: [hamburger menu][search]  — menu first, search second */
    const searchModal = iconsContainer.querySelector('details-modal.header__search');
    if (searchModal && !searchModal.dataset.ccMoved) {
      // Wrap the left-side area: drawer + search
      let leftWrap = header.querySelector('.header__left-group');
      if (!leftWrap) {
        leftWrap = document.createElement('div');
        leftWrap.className = 'header__left-group';
        // Insert as the FIRST child of header (left area)
        header.insertBefore(leftWrap, header.firstElementChild);
      }

      // Move drawer (hamburger) into wrapper FIRST
      const drawer = header.querySelector('header-drawer');
      if (drawer && drawer.parentElement !== leftWrap) {
        leftWrap.appendChild(drawer);
      }

      // Append search AFTER the drawer — menu comes first now
      leftWrap.appendChild(searchModal);
      searchModal.classList.add('header__search--left');
      searchModal.dataset.ccMoved = '1';
    } else if (searchModal && searchModal.dataset.ccMoved) {
      // Already moved — ensure order is [drawer, search]
      const leftWrap = header.querySelector('.header__left-group');
      const drawer = header.querySelector('header-drawer');
      if (leftWrap && drawer && leftWrap.firstElementChild !== drawer) {
        leftWrap.insertBefore(drawer, leftWrap.firstElementChild);
      }
      if (leftWrap && searchModal.parentElement === leftWrap && leftWrap.lastElementChild !== searchModal) {
        leftWrap.appendChild(searchModal);
      }
    }

    /* ─── 2) Create a language-toggle icon in place of the search ─── */
    if (!iconsContainer.querySelector('.header__icon--lang')) {
      // Determine current locale from <html lang="..">
      const htmlLang = (document.documentElement.lang || 'en').toLowerCase();
      const currentLocale = htmlLang.startsWith('ar') ? 'ar' : 'en';
      const targetLocale = currentLocale === 'ar' ? 'en' : 'ar';
      const targetLabel = targetLocale === 'ar' ? 'العربية' : 'English';
      // Arabic = "ع" (Arabic letter), English = "E" — no underline
      const shortLabel = targetLocale === 'ar' ? 'ع' : 'E';

      // Compute the path WITHOUT any current locale prefix so Shopify
      // can prepend the target locale cleanly. This ensures the locale
      // persists across subsequent navigations (cookie + URL prefix).
      function stripLocalePath() {
        let p = location.pathname;
        const root =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        if (root !== '/' && p.indexOf(root) === 0) {
          p = p.slice(root.length);
          if (p.charAt(0) !== '/') p = '/' + p;
        }
        // Extra safety: strip a leading /xx or /xx-xx locale segment if present
        p = p.replace(/^\/(ar|en|ar-[a-z]{2}|en-[a-z]{2})(\/|$)/i, '/');
        if (!p.startsWith('/')) p = '/' + p;
        return p + location.search;
      }

      // Build an invisible POST form for Shopify's /localization endpoint
      let form = document.getElementById('CCLocaleToggleForm');
      if (!form) {
        form = document.createElement('form');
        form.id = 'CCLocaleToggleForm';
        form.method = 'post';
        form.action = '/localization';
        form.style.display = 'none';
        form.innerHTML =
          '<input type="hidden" name="_method" value="put">' +
          '<input type="hidden" name="form_type" value="localization">' +
          '<input type="hidden" name="utf8" value="✓">' +
          '<input type="hidden" name="locale_code" value="' + targetLocale + '">' +
          '<input type="hidden" name="return_to" value="' +
            stripLocalePath().replace(/"/g, '&quot;') + '">';
        document.body.appendChild(form);
      }

      // Build the toggle button
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'header__icon header__icon--lang link focus-inset';
      btn.setAttribute('aria-label', 'Switch language to ' + targetLabel);
      btn.innerHTML =
        '<span class="header__icon-lang-circle" lang="' + targetLocale + '">' +
          '<span class="header__icon-lang-label">' + shortLabel + '</span>' +
        '</span>';

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        // round45: direct navigation is more reliable than POST /localization.
        // Shopify reads the URL prefix and sets the locale cookie
        // automatically on the first request with that prefix, so subsequent
        // navigation sticks to the chosen locale.
        const cleanPath = stripLocalePath(); // path without any locale prefix
        // Separate path from query for clean rebuilding
        const qIdx = cleanPath.indexOf('?');
        const pathOnly = qIdx === -1 ? cleanPath : cleanPath.slice(0, qIdx);
        const queryOnly = qIdx === -1 ? '' : cleanPath.slice(qIdx);
        let targetPath;
        if (targetLocale === 'ar') {
          targetPath = (pathOnly === '/' ? '/ar' : '/ar' + pathOnly) + queryOnly;
        } else {
          targetPath = pathOnly + queryOnly;
        }
        // Also POST to /localization in the background so Shopify sets the
        // locale cookie persistently — but don't block navigation on it.
        try {
          const fd = new FormData();
          fd.append('_method', 'put');
          fd.append('form_type', 'localization');
          fd.append('utf8', '\u2713');
          fd.append('locale_code', targetLocale);
          fd.append('return_to', targetPath);
          navigator.sendBeacon && navigator.sendBeacon('/localization', fd);
        } catch (err) {}
        // Navigate immediately — Shopify will honour the URL prefix and
        // set the locale cookie on this request.
        window.location.assign(targetPath);
      });

      // Insert as FIRST item in header__icons (where the search used to be)
      const firstChild = iconsContainer.firstElementChild;
      if (firstChild) {
        iconsContainer.insertBefore(btn, firstChild);
      } else {
        iconsContainer.appendChild(btn);
      }

      // Hide the default desktop-localization-wrapper if present
      const dlw = iconsContainer.querySelector('.desktop-localization-wrapper');
      if (dlw) dlw.classList.add('cc-hidden');
    }
  }

  /* ─── Resolve a theme asset URL by using an existing asset link as anchor ─── */
  function getThemeAssetUrl(filename) {
    const existing = document.querySelector('link[href*="custom-cartier.css"], script[src*="custom-cartier.js"]');
    if (existing) {
      const href = existing.href || existing.src || '';
      const m = href.match(/(.*\/assets\/)/);
      if (m) return m[1] + filename;
    }
    return '/cdn/shop/t/2/assets/' + filename;
  }

  /* ─── Hero banner: inject the ORIGINAL high-res logo image + full-banner home link ─── */
  function wireBannerHomeLink() {
    const banners = document.querySelectorAll(
      '[id*="hero_banner"] .banner, [id*="image_banner"] .banner, [id*="image-banner"] .banner'
    );
    banners.forEach(function (banner) {
      if (banner.dataset.ccHomeLinked) return;

      // Inject the original high-res Mahsool logo image (replaces low-res CDN image)
      if (!banner.querySelector('.banner__brand')) {
        const brand = document.createElement('div');
        brand.className = 'banner__brand';
        const img = document.createElement('img');
        img.src = getThemeAssetUrl('mahsool-logo-text.png');
        img.alt = 'Mahsool — محصول';
        img.className = 'banner__brand-img';
        img.decoding = 'async';
        img.loading = 'eager';
        brand.appendChild(img);
        banner.appendChild(brand);
      }

      // Compute homepage URL, respecting current locale prefix (e.g. /ar)
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const a = document.createElement('a');
      a.href = rootPath;
      a.className = 'banner__home-link';
      const _isArHome = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
      const _homeLabel = _isArHome ? 'محصول — الرئيسية' : 'Mahsool — Home';
      const _homeText = _isArHome ? 'الرئيسية' : 'Home';
      a.setAttribute('aria-label', _homeLabel);
      a.innerHTML = '<span>' + _homeText + '</span>';
      banner.appendChild(a);
      banner.dataset.ccHomeLinked = '1';
    });
  }

  /* ─── Insert "Shop by Brands" CTA between hero banner and collection list ─── */
  function injectBrandsCTA() {
    if (document.querySelector('.cc-brands-cta')) return;
    const banner = document.querySelector('[id*="hero_banner"], [id*="image_banner"], [id*="image-banner"]');
    if (!banner) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const brandsHref = (root.endsWith('/') ? root : root + '/') + 'pages/brands';
    const isAr = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
    const eyebrow = isAr ? 'استكشف' : 'Discover';
    const tagline = isAr
      ? 'رحلةٌ من الحبّة إلى الفنجان، مصاغةٌ بشغف'
      : 'A journey from bean to cup, crafted with passion';
    const label = isAr ? 'تسوّق حسب العلامة التجارية' : 'Shop by Brands';
    const arrow = isAr ? '←' : '→';

    const cta = document.createElement('section');
    cta.className = 'cc-brands-cta';
    cta.setAttribute('aria-label', label);
    cta.innerHTML =
      '<span class="cc-brands-cta__eyebrow">' + eyebrow + '</span>' +
      '<p class="cc-brands-cta__tagline">' + tagline + '</p>' +
      '<a class="cc-brands-cta__btn" href="' + brandsHref + '">' +
        '<span class="cc-brands-cta__label">' + label + '</span>' +
        '<span class="cc-brands-cta__arrow" aria-hidden="true">' + arrow + '</span>' +
      '</a>';
    banner.parentNode.insertBefore(cta, banner.nextSibling);
  }

  /* ─── Inject elegant Page 01 / Page 02 pagination.
        Appears on BOTH the home page (Page 01 active) and the brands page
        (Page 02 active). Acts as a cross-link between the two. ─── */
  function injectHomePagination() {
    if (document.querySelector('.cc-home-pagination')) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const rootSlash = root.endsWith('/') ? root : root + '/';
    const brandsHref = rootSlash + 'pages/brands';
    const homeHref = rootSlash;

    // Detect which page we're on
    const isHome =
      location.pathname === '/' ||
      location.pathname === '/ar' ||
      location.pathname === '/ar/' ||
      /^\/(ar\/)?$/.test(location.pathname);
    const isBrandsPage =
      /\/pages\/brands(\/|$|\?)/.test(location.pathname + location.search) ||
      document.body.classList.contains('cc-page-brands');

    if (!isHome && !isBrandsPage) return;

    // Clear any stale hiding class from earlier rounds
    document
      .querySelectorAll('.collection-list__item.cc-page-hidden')
      .forEach(function (li) {
        li.classList.remove('cc-page-hidden');
      });

    // Pick an anchor element — the pagination is inserted right after it
    let anchor = null;
    if (isHome) {
      const collectionSection = document.querySelector(
        '[id*="collection_list"], .section-collection-list'
      );
      if (!collectionSection) return;
      anchor = collectionSection.closest('.shopify-section') || collectionSection;
    } else {
      // Brands page: anchor after the main brand-grid section so pagination appears
      // at the bottom of the brands listing.
      const main = document.getElementById('MainContent') || document.querySelector('main');
      if (!main) return;
      const lastSection =
        main.querySelector('.shopify-section:last-of-type') ||
        main.querySelector('.shopify-section') ||
        main;
      anchor = lastSection;
    }
    if (!anchor) return;

    const isAr = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
    const pageLabel = isAr ? 'الصفحة' : 'Page';
    const prevLabel = isAr ? 'السابق' : 'Prev';
    const nextLabel = isAr ? 'التالي' : 'Next';

    // Compute href/active state per page based on current location
    const page1Active = isHome;
    const page2Active = isBrandsPage;
    const page1Href = page1Active ? '#' : homeHref;
    const page2Href = page2Active ? '#' : brandsHref;
    const prevDisabled = page1Active; // on home → prev is disabled
    const nextDisabled = page2Active; // on brands → next is disabled
    const prevHref = page1Active ? '#' : homeHref; // on brands → prev goes home
    const nextHref = page2Active ? '#' : brandsHref; // on home → next goes to brands

    const nav = document.createElement('nav');
    nav.className = 'cc-home-pagination';
    nav.setAttribute('aria-label', pageLabel);
    nav.innerHTML =
      '<a class="cc-home-pagination__arrow cc-home-pagination__arrow--prev' +
        (prevDisabled ? ' is-disabled' : '') +
        '" href="' + prevHref + '"' +
        (prevDisabled ? ' aria-disabled="true" tabindex="-1"' : '') +
        ' aria-label="' + prevLabel + '">' +
        '<span aria-hidden="true">‹</span>' +
      '</a>' +
      '<div class="cc-home-pagination__pages">' +
        '<a class="cc-home-pagination__page' +
          (page1Active ? ' is-active' : '') +
          '" data-page="1" href="' + page1Href + '"' +
          (page1Active ? ' aria-current="page"' : '') + '>' +
          '<span class="cc-home-pagination__page-label">' + pageLabel + '</span>' +
          '<span class="cc-home-pagination__page-num">01</span>' +
        '</a>' +
        '<span class="cc-home-pagination__divider" aria-hidden="true"></span>' +
        '<a class="cc-home-pagination__page' +
          (page2Active ? ' is-active' : '') +
          '" data-page="2" href="' + page2Href + '"' +
          (page2Active ? ' aria-current="page"' : '') + '>' +
          '<span class="cc-home-pagination__page-label">' + pageLabel + '</span>' +
          '<span class="cc-home-pagination__page-num">02</span>' +
        '</a>' +
      '</div>' +
      '<a class="cc-home-pagination__arrow cc-home-pagination__arrow--next' +
        (nextDisabled ? ' is-disabled' : '') +
        '" href="' + nextHref + '"' +
        (nextDisabled ? ' aria-disabled="true" tabindex="-1"' : '') +
        ' aria-label="' + nextLabel + '">' +
        '<span aria-hidden="true">›</span>' +
      '</a>';

    anchor.parentNode.insertBefore(nav, anchor.nextSibling);

    // Active-page clicks should just scroll to top (or to anchor) smoothly
    nav.querySelectorAll('.cc-home-pagination__page.is-active, .cc-home-pagination__arrow.is-disabled').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        if (el.classList.contains('is-active')) {
          try {
            (anchor || document.body).scrollIntoView({ behavior: 'smooth', block: 'start' });
          } catch (_) {}
        }
      });
    });
    // Inactive links are real anchors → browser handles navigation
  }

  /* ─── Inject a homepage-style dark banner on content pages that lack hero_banner ─── */
  function injectPageBanner() {
    // Don't inject if a real banner already exists (homepage)
    if (document.querySelector('[id*="hero_banner"], [id*="image_banner"], [id*="image-banner"]')) return;
    if (document.querySelector('.cc-page-banner')) return;

    const main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;

    // Detect the brands page via content marker or URL
    const isBrandsPage =
      !!document.querySelector('.mahsool-section-heading') ||
      /\/pages\/brands(\/|$|\?)/.test(location.pathname + location.search);
    if (isBrandsPage) {
      document.body.classList.add('cc-page-brands');
      // Strip inline style="..." from all .rte descendants so our CSS owns the layout.
      // Shopify's page editor injects inline flex/max-width/display/color values that
      // beat !important rules via inline-style specificity for unflagged props.
      const rte = main.querySelector('.rte');
      if (rte && !rte.dataset.ccStripped) {
        const targets = [rte, ...rte.querySelectorAll('*')];
        targets.forEach(function (el) {
          if (el.hasAttribute('style')) el.removeAttribute('style');
        });
        rte.dataset.ccStripped = '1';
      }
    }

    const rootPath =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const logoSrc = getThemeAssetUrl('mahsool-logo-text.png');

    const _isArPb = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
    const _pbHomeAria = _isArPb ? 'محصول — الرئيسية' : 'Mahsool — Home';
    const _pbHomeText = _isArPb ? 'الرئيسية' : 'Home';

    const banner = document.createElement('section');
    banner.className = 'cc-page-banner';
    banner.setAttribute('aria-label', 'Mahsool');
    banner.innerHTML =
      '<div class="banner__brand">' +
        '<img class="banner__brand-img" src="' + logoSrc + '" alt="Mahsool — محصول" decoding="async" loading="eager">' +
      '</div>' +
      '<a class="banner__home-link" href="' + rootPath + '" aria-label="' + _pbHomeAria + '"><span>' + _pbHomeText + '</span></a>';

    main.insertBefore(banner, main.firstChild);
  }

  /* ─── Remove any empty footer blocks (e.g. an unconfigured "brand info"
        block that renders with just an empty <p>). Keeping empty blocks
        would leave a blank column and make the footer look unbalanced. ─── */
  function removeEmptyFooterBlocks() {
    const wrap = document.querySelector('.footer .footer__blocks-wrapper');
    if (!wrap) return;
    Array.from(wrap.children).forEach(function (block) {
      if (!block.classList.contains('footer-block')) return;
      const text = (block.textContent || '').replace(/\s+/g, '').trim();
      const hasInteractive = block.querySelector(
        'a, button, input, textarea, select, img, svg, form, ul, ol'
      );
      if (!text && !hasInteractive) {
        block.remove();
      }
    });
  }

  /* ─── Inject a Language column into the footer, aligned with the
        Quick Links / Collections headings. Appears on every page. ─── */
  function injectFooterLangBlock() {
    if (document.querySelector('.cc-footer-lang-block')) return;
    const footer = document.querySelector('footer.footer');
    if (!footer) return;
    const wrap = footer.querySelector('.footer__blocks-wrapper, .grid');
    if (!wrap) return;

    const isAr = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
    const heading = isAr ? 'اللغة' : 'Language';
    const arLabel = 'العربية';
    const enLabel = 'English';
    const currentLang = isAr ? 'ar' : 'en';

    const block = document.createElement('div');
    block.className = 'footer-block grid__item cc-footer-lang-block';
    block.innerHTML =
      '<h2 class="footer-block__heading inline-richtext">' + heading + '</h2>' +
      '<ul class="footer-block__details-content list-unstyled cc-footer-lang-list">' +
        '<li>' +
          '<button type="button" class="link link--text list-menu__item list-menu__item--link cc-lang-btn' +
            (currentLang === 'ar' ? ' is-active' : '') + '" data-cc-lang="ar">' +
            arLabel +
          '</button>' +
        '</li>' +
        '<li>' +
          '<button type="button" class="link link--text list-menu__item list-menu__item--link cc-lang-btn' +
            (currentLang === 'en' ? ' is-active' : '') + '" data-cc-lang="en">' +
            enLabel +
          '</button>' +
        '</li>' +
      '</ul>';

    // Insert as the first block so it appears at the start (rightmost in
    // LTR, leftmost in RTL) alongside Quick Links / Collections.
    wrap.insertBefore(block, wrap.firstChild);

    // Wire up the buttons to use the native Dawn localization form.
    block.querySelectorAll('.cc-lang-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const target = btn.getAttribute('data-cc-lang') || 'ar';
        if (target === currentLang) return;
        const nativeLink = document.querySelector(
          'a[hreflang="' + target + '"][data-value="' + target + '"]'
        );
        if (nativeLink) {
          nativeLink.click();
          return;
        }
        // Fallback: post to /localization directly
        let returnTo = location.pathname + location.search;
        returnTo = returnTo.replace(/^\/(ar|en)(\/|$)/, '/');
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/localization';
        form.style.display = 'none';
        form.innerHTML =
          '<input type="hidden" name="form_type" value="localization">' +
          '<input type="hidden" name="utf8" value="✓">' +
          '<input type="hidden" name="_method" value="put">' +
          '<input type="hidden" name="return_to" value="' + returnTo + '">' +
          '<input type="hidden" name="locale_code" value="' + target + '">';
        document.body.appendChild(form);
        form.submit();
      });
    });
  }

  /* ─── Quick-Add: floating cart button on every product card ─── */
  const CC_CART_ICON_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="9" cy="20" r="1.2"></circle>' +
      '<circle cx="18" cy="20" r="1.2"></circle>' +
      '<path d="M2.5 3h2.2l2.6 12.1a2 2 0 0 0 2 1.6h8.5a2 2 0 0 0 2-1.5l1.6-7.2H6.2"></path>' +
      '<path d="M19 6.5v-3M17.5 5h3"></path>' +
    '</svg>';

  const CC_CHECK_ICON_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="20 6 9 17 4 12"></polyline>' +
    '</svg>';

  function setButtonIcon(btn, state) {
    if (state === 'added') btn.innerHTML = CC_CHECK_ICON_SVG;
    else btn.innerHTML = CC_CART_ICON_SVG;
  }

  async function ccUpdateCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const _isArCart = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
      const _itemsWord = _isArCart ? 'عناصر' : 'items';
      // Update all cart-count bubbles on the page
      const bubbles = document.querySelectorAll('.cart-count-bubble');
      bubbles.forEach(function (bubble) {
        // Dawn/Trade theme structure: <div class="cart-count-bubble"><span aria-hidden="true">N</span><span class="visually-hidden">N items</span></div>
        const visible = bubble.querySelector('span[aria-hidden="true"]');
        if (visible) visible.textContent = cart.item_count;
        const vh = bubble.querySelector('.visually-hidden');
        if (vh) vh.textContent = cart.item_count + ' ' + _itemsWord;
        bubble.style.display = cart.item_count > 0 ? '' : 'none';
      });
      // If no bubble exists yet but cart has items, try to create one inside cart icon link
      if (bubbles.length === 0 && cart.item_count > 0) {
        const cartLink = document.querySelector('a.header__icon--cart, a[href$="/cart"]');
        if (cartLink && !cartLink.querySelector('.cart-count-bubble')) {
          const b = document.createElement('div');
          b.className = 'cart-count-bubble';
          b.innerHTML = '<span aria-hidden="true">' + cart.item_count + '</span>' +
                        '<span class="visually-hidden">' + cart.item_count + ' ' + _itemsWord + '</span>';
          cartLink.appendChild(b);
        }
      }
      // Dispatch a synthetic event so any theme listeners can react
      document.dispatchEvent(new CustomEvent('cc:cart:updated', { detail: cart }));
    } catch (e) { /* silent */ }
  }

  async function ccAddVariantById(variantId, btn) {
    btn.classList.remove('cc-quick-add--added', 'cc-quick-add--error');
    btn.classList.add('cc-quick-add--loading');
    btn.disabled = true;
    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!addRes.ok) {
        const err = await addRes.json().catch(function () { return {}; });
        throw new Error(err.description || 'add failed');
      }
      await ccUpdateCartCount();
      btn.classList.remove('cc-quick-add--loading');
      btn.classList.add('cc-quick-add--added');
      setButtonIcon(btn, 'added');
      setTimeout(function () {
        btn.classList.remove('cc-quick-add--added');
        setButtonIcon(btn, 'cart');
        btn.disabled = false;
      }, 1400);
    } catch (e) {
      btn.classList.remove('cc-quick-add--loading');
      btn.classList.add('cc-quick-add--error');
      setTimeout(function () {
        btn.classList.remove('cc-quick-add--error');
        btn.disabled = false;
      }, 1600);
    }
  }

  async function ccAddToCart(handle, btn) {
    btn.classList.remove('cc-quick-add--added', 'cc-quick-add--error');
    btn.classList.add('cc-quick-add--loading');
    btn.disabled = true;
    try {
      // Fetch product JSON to resolve first available variant
      const pRes = await fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } });
      if (!pRes.ok) throw new Error('product fetch failed');
      const product = await pRes.json();
      const variant =
        (product.variants || []).find(function (v) { return v.available; }) ||
        (product.variants || [])[0];
      if (!variant) throw new Error('no variant');

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
      });
      if (!addRes.ok) {
        const err = await addRes.json().catch(function () { return {}; });
        throw new Error(err.description || 'add failed');
      }
      await ccUpdateCartCount();
      btn.classList.remove('cc-quick-add--loading');
      btn.classList.add('cc-quick-add--added');
      setButtonIcon(btn, 'added');
      setTimeout(function () {
        btn.classList.remove('cc-quick-add--added');
        setButtonIcon(btn, 'cart');
        btn.disabled = false;
      }, 1400);
    } catch (e) {
      btn.classList.remove('cc-quick-add--loading');
      btn.classList.add('cc-quick-add--error');
      setTimeout(function () {
        btn.classList.remove('cc-quick-add--error');
        btn.disabled = false;
      }, 1600);
    }
  }

  /* ─── Product page: replace each variant row's +/- with a cart button ─── */
  function injectVariantCartButtons() {
    const cells = document.querySelectorAll('td.variant-item__quantity, .variant-item__quantity');
    cells.forEach(function (cell) {
      if (cell.dataset.ccVariantBtn === '1') return;

      // Find the variant ID from the hidden quantity input BEFORE hiding anything
      const qInput = cell.querySelector('input[data-quantity-variant-id], input[data-index], input[name="updates[]"]');
      let variantId = null;
      if (qInput) {
        variantId =
          qInput.getAttribute('data-quantity-variant-id') ||
          qInput.getAttribute('data-index') ||
          (qInput.id || '').replace(/^Quantity-/, '');
      }
      // Fallback: look for data-variant-id anywhere inside the row
      if (!variantId) {
        const row = cell.closest('tr, .variant-item');
        if (row) {
          const any = row.querySelector('[data-variant-id], [data-quantity-variant-id]');
          if (any) {
            variantId =
              any.getAttribute('data-variant-id') ||
              any.getAttribute('data-quantity-variant-id');
          }
        }
      }
      if (!variantId) return;

      // HARD HIDE the native quantity UI via inline styles (fail-safe
      // even if CSS doesn't reach the browser yet). Does NOT remove from
      // DOM — only hides visually — so the cart page's quantity logic
      // remains intact.
      const hideTargets = cell.querySelectorAll(
        'quantity-popover, .quantity-popover, .quantity-popover-wrapper, ' +
        '.quantity-popover-container, quantity-input, .quantity, ' +
        '.quantity__button, .quantity__input, .quantity__label, ' +
        '.progress-bar-container, .variant-item__error'
      );
      hideTargets.forEach(function (el) {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
      });

      // Create the cart button
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cc-variant-cart-btn';
      btn.setAttribute('aria-label', 'Add to cart');
      btn.dataset.variantId = variantId;
      setButtonIcon(btn, 'cart');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        ccAddVariantById(variantId, btn);
      });

      cell.appendChild(btn);
      cell.dataset.ccVariantBtn = '1';
    });
  }

  function injectQuickAdd() {
    // Find all product anchors (exclude collection/page/brand links)
    const productLinks = document.querySelectorAll('a[href*="/products/"]');
    const seenCards = new WeakSet();

    productLinks.forEach(function (link) {
      // Skip the product page's own main links — only target card-style contexts
      // Find the nearest card-like container
      const card =
        link.closest('.card-wrapper') ||
        link.closest('.card') ||
        link.closest('li.grid__item') ||
        link.closest('.grid__item') ||
        link.closest('.product-card-wrapper');
      if (!card) return;
      if (seenCards.has(card)) return;
      if (card.dataset.ccQuickAdd === '1') { seenCards.add(card); return; }

      // Only inside product/collection grids — skip if this is a recommendation
      // inside a cart drawer (cart drawer has its own add flow)
      if (card.closest('cart-drawer, #cart-drawer, .cart-drawer')) return;

      // Extract handle from href
      const hrefAttr = link.getAttribute('href') || '';
      const m = hrefAttr.match(/\/products\/([^/?#]+)/);
      if (!m) return;
      const handle = m[1];

      // Build button
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cc-quick-add';
      btn.setAttribute('aria-label', 'Add to cart');
      btn.dataset.handle = handle;
      setButtonIcon(btn, 'cart');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        ccAddToCart(handle, btn);
      });
      // Block the click from bubbling to the card's anchor
      btn.addEventListener('mousedown', function (e) { e.stopPropagation(); });
      btn.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });

      // Mount the button OUTSIDE the card-wrapper as a sibling below it.
      // The card-wrapper in Shopify's Trade theme uses overflow:hidden to
      // crop the image, so any button placed inside it gets clipped.
      // Insert the button AFTER the card-wrapper so it shows on the
      // OUTSIDE — user doesn't need to click into the product to add.
      const gridItem = card.closest('li.grid__item, .grid__item, .product-card-wrapper');
      const mount = gridItem && gridItem !== card ? gridItem : card.parentElement;
      if (mount && card.parentElement === mount) {
        // Insert as a sibling directly after the card-wrapper
        if (card.nextSibling) {
          mount.insertBefore(btn, card.nextSibling);
        } else {
          mount.appendChild(btn);
        }
      } else if (gridItem) {
        gridItem.appendChild(btn);
      } else {
        // Fallback — insert after the card's parent if possible
        card.parentElement && card.parentElement.appendChild(btn);
      }

      card.dataset.ccQuickAdd = '1';
      seenCards.add(card);
    });
  }

  /* ─── Make entire collection card clickable (stretched-link fallback) ───
     Shopify's Trade theme wraps ONLY the heading text in an anchor,
     not the image or the full card. This makes the whole card-wrapper
     respond to clicks by delegating to the inner anchor's href. */
  function makeCollectionCardsClickable() {
    const wrappers = document.querySelectorAll(
      '.collection-list .card-wrapper, ' +
      '.collection-list .collection-card-wrapper, ' +
      '.collection-list__item .card-wrapper'
    );
    wrappers.forEach(function (wrap) {
      if (wrap.dataset.ccClickable === '1') return;

      // Find the destination URL from the inner anchor
      const a =
        wrap.querySelector('.card__heading a[href]') ||
        wrap.querySelector('a[href*="/collections/"]') ||
        wrap.querySelector('a[href]');
      if (!a || !a.href) return;
      const href = a.href;
      const target = a.target || '';

      wrap.style.cursor = 'pointer';
      wrap.setAttribute('role', 'link');
      wrap.setAttribute('tabindex', '0');
      wrap.setAttribute('aria-label', (a.textContent || '').trim());

      wrap.addEventListener('click', function (e) {
        // Don't hijack clicks on nested real anchors/buttons
        const t = e.target;
        if (t && t.closest && t.closest('a, button, [role="button"]')) {
          return; // let native click happen
        }
        e.preventDefault();
        if (target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey) {
          window.open(href, target || '_blank');
        } else {
          window.location.href = href;
        }
      });

      wrap.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          window.location.href = href;
        }
      });

      wrap.dataset.ccClickable = '1';
    });
  }

  /* ─── Bilingual text swap for every static label on the site.
        Product names/descriptions are intentionally NOT touched — those
        must be translated via Shopify Translate & Adapt.
        Brand names (Fellow, Hario, 1Zpresso, ...) are international
        trademarks and preserved as-is.
        ─────────────────────────────────────────────────────────────── */
  const CC_DICT = {
    /* ── Navigation / Collections / Menu ── */
    'home':             'الرئيسية',
    'shop':             'المتجر',
    'brands':           'العلامات التجارية',
    'brand':            'العلامة التجارية',
    'coffee':           'القهوة',
    'coffee beans':     'حبوب القهوة',
    'beans':            'حبوب القهوة',
    'espresso machines':'مكائن الإسبريسو',
    'espresso machine': 'مكينة الإسبريسو',
    'grinders':         'المطاحن',
    'grinder':          'مطحنة',
    'brewing tools':    'أدوات التحضير',
    'brewers':          'أدوات التحضير',
    'cups & mugs':      'الأكواب والفناجين',
    'cups and mugs':    'الأكواب والفناجين',
    'cups':             'الأكواب',
    'mugs':             'الفناجين',
    'barista tools':    'أدوات الباريستا',
    'accessories':      'الإكسسوارات',
    'matcha & tea':     'الماتشا والشاي',
    'matcha and tea':   'الماتشا والشاي',
    'matcha':           'الماتشا',
    'tea':              'الشاي',
    'cleaners':         'منتجات التنظيف',
    'cleaning':         'التنظيف',
    'filters':          'الفلاتر',
    'scales':           'الموازين',
    'kettles':          'الغلايات',
    'all products':     'كل المنتجات',
    'new arrivals':     'الإصدارات الجديدة',
    'best sellers':     'الأكثر مبيعاً',
    'sale':             'تخفيضات',
    'on sale':          'في التخفيضات',

    /* ── Footer column headings ── */
    'quick links':      'روابط سريعة',
    'collections':      'التصنيفات',
    'language':         'اللغة',
    'information':      'معلومات',
    'customer service': 'خدمة العملاء',
    'newsletter':       'النشرة البريدية',

    /* ── Footer links ── */
    'about us':         'من نحن',
    'about':            'حولنا',
    'contact us':       'تواصل معنا',
    'contact':          'اتصل بنا',
    'search':           'البحث',
    'privacy policy':   'سياسة الخصوصية',
    'terms of service': 'شروط الخدمة',
    'refund policy':    'سياسة الاسترجاع',
    'shipping policy':  'سياسة الشحن',
    'shipping & delivery': 'الشحن والتوصيل',
    'shipping and delivery': 'الشحن والتوصيل',
    'shipping':         'الشحن',
    'delivery':         'التوصيل',
    'faq':              'الأسئلة الشائعة',
    'faqs':             'الأسئلة الشائعة',
    'frequently asked questions': 'الأسئلة الشائعة',

    /* ── Cart / Checkout ── */
    'sold out':         'نفد المخزون',
    'out of stock':     'غير متوفر',
    'in stock':         'متوفر',
    'add to cart':      'إضافة إلى السلة',
    'add to bag':       'إضافة إلى الحقيبة',
    'view details':     'عرض التفاصيل',
    'quick view':       'عرض سريع',
    'quick add':        'إضافة سريعة',
    'continue shopping':'متابعة التسوق',
    'your cart is empty': 'سلتك فارغة',
    'cart is empty':    'السلة فارغة',
    'shopping cart':    'سلة التسوق',
    'cart':             'السلة',
    'checkout':         'إتمام الشراء',
    'subtotal':         'المجموع الفرعي',
    'total':            'الإجمالي',
    'free':             'مجاني',
    'quantity':         'الكمية',
    'price':            'السعر',
    'update':           'تحديث',
    'remove':           'إزالة',

    /* ── Account / Auth ── */
    'log in':           'تسجيل الدخول',
    'login':            'تسجيل الدخول',
    'sign in':          'تسجيل الدخول',
    'log out':          'تسجيل الخروج',
    'sign out':         'تسجيل الخروج',
    'create account':   'إنشاء حساب',
    'register':         'تسجيل',
    'account':          'الحساب',
    'my account':       'حسابي',
    'email':            'البريد الإلكتروني',
    'password':         'كلمة المرور',
    'forgot password':  'نسيت كلمة المرور',

    /* ── Search / Misc ── */
    'search...':        'بحث...',
    'search our store': 'ابحث في متجرنا',
    'no results found': 'لا توجد نتائج',
    'view all':         'عرض الكل',
    'see all':          'مشاهدة الكل',
    'show more':        'عرض المزيد',
    'show less':        'عرض أقل',
    'load more':        'تحميل المزيد',
    'filter':           'تصفية',
    'filters':          'الفلاتر',
    'sort by':          'ترتيب حسب',
    'sort':             'ترتيب',
    'clear':            'مسح',
    'clear all':        'مسح الكل',
    'apply':            'تطبيق',

    /* ── Brands page specific ── */
    'shop by brands':   'تسوّق حسب العلامة التجارية',
    'shop by brand':    'تسوّق حسب العلامة التجارية',
    "shop from the world's best coffee equipment brands": 'تسوّق من أرقى علامات معدات القهوة في العالم',
    'shop from the worlds best coffee equipment brands':  'تسوّق من أرقى علامات معدات القهوة في العالم',
    '← back to category': '← العودة إلى التصنيفات',
    'back to category':   'العودة إلى التصنيفات',

    /* ── Newsletter ── */
    'stay updated':     'ابق على اطلاع',
    'subscribe':        'اشتراك',
    'subscribe now':    'اشترك الآن',
    'your email':       'بريدك الإلكتروني'
  };

  // Reverse map: Arabic → English (for when switching from AR → EN)
  const CC_DICT_REV = (function () {
    const r = {};
    Object.keys(CC_DICT).forEach(function (en) {
      const ar = CC_DICT[en];
      // Capitalize first letter of the English version for cleanliness
      const enCap = en.charAt(0).toUpperCase() + en.slice(1);
      if (!r[ar]) r[ar] = enCap;
    });
    return r;
  })();

  function ccNormalize(t) {
    return (t || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }
  function ccNormalizeAr(t) {
    return (t || '').replace(/\s+/g, ' ').trim();
  }

  function applyBilingualLabels() {
    const isAr = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');

    // ─── Zone 1: Brands page specific swaps ───────────────────────────
    const onBrandsPage =
      /\/pages\/brands(\/|$|\?)/.test(location.pathname + location.search) ||
      document.body.classList.contains('cc-page-brands');

    if (onBrandsPage) {
      // Brands page h1 (comes from Shopify page title)
      document.querySelectorAll('h1.main-page-title, .main-page-title, h1.page-title').forEach(function (el) {
        const n = ccNormalize(el.textContent);
        if (n === 'brands' || n === 'brand') {
          el.textContent = isAr ? 'العلامات التجارية' : 'Brands';
        }
      });
    }

    // ─── Zone 2: Collection title (collection-hero) ───────────────────
    document.querySelectorAll('.collection-hero__title, h1.collection-hero__title').forEach(function (el) {
      const n = ccNormalize(el.textContent);
      if (isAr && CC_DICT[n]) {
        el.textContent = CC_DICT[n];
      } else if (!isAr && CC_DICT_REV[ccNormalizeAr(el.textContent)]) {
        el.textContent = CC_DICT_REV[ccNormalizeAr(el.textContent)];
      }
    });

    // ─── Zone 3: Header main menu items (summary + inline links) ──────
    document.querySelectorAll(
      '.header__inline-menu a, .header__inline-menu summary, ' +
      '.header__submenu a, .menu-drawer a, .menu-drawer summary'
    ).forEach(function (el) {
      // Find the innermost text node / span
      const span = el.querySelector('span');
      const target = (span && !span.querySelector('svg,img')) ? span : el;
      if (target.querySelector('svg, img, .icon, ul, li')) return; // skip composite
      ccSwapTextNode(target, isAr);
    });

    // ─── Zone 4: Footer blocks — headings + links ─────────────────────
    document.querySelectorAll(
      '.footer .footer-block__heading, .footer .footer-block__details-content a, ' +
      '.footer__content-bottom a, .copyright a'
    ).forEach(function (el) {
      if (el.closest('.cc-footer-lang-block')) return; // we own this block already
      ccSwapTextNode(el, isAr);
    });

    // ─── Zone 5: Breadcrumb items ─────────────────────────────────────
    document.querySelectorAll('.breadcrumb a, .breadcrumb__link').forEach(function (el) {
      ccSwapTextNode(el, isAr);
    });

    // ─── Zone 6: Page title h1 on content pages (About Us, Contact, etc.)
    document.querySelectorAll('h1.main-page-title, h1.page-title').forEach(function (el) {
      ccSwapTextNode(el, isAr);
    });

    // ─── Zone 7: Generic buttons & labels (sold out, add to cart, etc.)
    document.querySelectorAll(
      'button, .btn, .button, a.button, a.btn, .badge, .cart__empty-text, ' +
      '.quick-add__submit, .product-form__submit, .shopify-payment-button__button, ' +
      '.totals__total-value + *, .totals__subtotal, .totals__total, ' +
      '.cart__checkout-button, .cart__update-button'
    ).forEach(function (el) {
      if (el.querySelector('svg, img, input')) {
        // Only operate on text-only child spans
        el.querySelectorAll('span').forEach(function (sp) {
          if (!sp.querySelector('svg, img')) ccSwapTextNode(sp, isAr);
        });
        return;
      }
      ccSwapTextNode(el, isAr);
    });

    // ─── Zone 8: Brands-page subtitle / labels embedded in page rte ───
    if (onBrandsPage) {
      document.querySelectorAll('.mahsool-section-heading__title').forEach(function (el) {
        ccSwapTextNode(el, isAr);
      });
      document.querySelectorAll('.mahsool-section-heading__subtitle').forEach(function (el) {
        ccSwapTextNode(el, isAr);
      });
      // "← Back to Category" link — find by text pattern
      document.querySelectorAll('a').forEach(function (a) {
        const n = ccNormalize(a.textContent);
        if (n === '← back to category' || n === 'back to category') {
          a.textContent = isAr ? '← العودة إلى التصنيفات' : '← Back to Category';
        }
      });
    }
  }

  function ccSwapTextNode(el, isAr) {
    if (!el || !el.textContent) return;
    const raw = el.textContent;
    if (isAr) {
      const key = ccNormalize(raw);
      if (CC_DICT[key] && CC_DICT[key] !== raw.trim()) {
        el.textContent = CC_DICT[key];
      }
    } else {
      const key = ccNormalizeAr(raw);
      if (CC_DICT_REV[key] && CC_DICT_REV[key] !== raw.trim()) {
        el.textContent = CC_DICT_REV[key];
      }
    }
  }

  /* ─── Inject "All Products / كل المنتجات" link at the top of the
        hamburger drawer menu and the desktop inline header menu.   ─── */
  function injectAllProductsMenuItem() {
    const isAr = (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
    const label = isAr ? 'كل المنتجات' : 'All Products';
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const href = (root.endsWith('/') ? root : root + '/') + 'collections/all';

    // 1) Hamburger drawer menu
    document.querySelectorAll('ul.menu-drawer__menu').forEach(function (ul) {
      if (ul.querySelector('[data-cc-all-products]')) return;
      const li = document.createElement('li');
      li.innerHTML =
        '<a ' +
          'data-cc-all-products="1" ' +
          'href="' + href + '" ' +
          'class="menu-drawer__menu-item list-menu__item link link--text focus-inset">' +
          label +
        '</a>';
      ul.insertBefore(li, ul.firstChild);
    });

    // 2) Desktop inline header menu (header__inline-menu)
    document.querySelectorAll('ul.list-menu.list-menu--inline, .header__inline-menu > ul').forEach(function (ul) {
      if (ul.querySelector('[data-cc-all-products]')) return;
      const li = document.createElement('li');
      li.innerHTML =
        '<a ' +
          'data-cc-all-products="1" ' +
          'href="' + href + '" ' +
          'class="header__menu-item list-menu__item link link--text focus-inset">' +
          '<span>' + label + '</span>' +
        '</a>';
      ul.insertBefore(li, ul.firstChild);
    });
  }

  /* ==========================================================================
     ROUND 57 — Premium features
     Wishlist, Predictive Search, Review Stars, Back-in-Stock,
     Instagram strip, About/Tracking/Policy/Wishlist page renderers.
     ========================================================================== */

  const CC_INSTAGRAM_HANDLE = 'mahsool.kw';

  function ccIsAr() {
    return (document.documentElement.lang || 'ar').toLowerCase().startsWith('ar');
  }
  function ccRootSlash() {
    const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : r + '/';
  }
  function ccT(ar, en) { return ccIsAr() ? ar : en; }

  /* ───────── 57.1 WISHLIST ───────── */
  const CC_WL_KEY = 'cc_wishlist_v1';
  function ccGetWL() {
    try { return JSON.parse(localStorage.getItem(CC_WL_KEY) || '[]'); }
    catch (_) { return []; }
  }
  function ccSetWL(arr) {
    try { localStorage.setItem(CC_WL_KEY, JSON.stringify(arr)); } catch (_) {}
    ccUpdateWLBadge();
  }
  function ccToggleWL(handle) {
    const list = ccGetWL();
    const idx = list.indexOf(handle);
    if (idx === -1) list.push(handle); else list.splice(idx, 1);
    ccSetWL(list);
    return idx === -1;
  }
  function ccInWL(handle) { return ccGetWL().indexOf(handle) !== -1; }
  function ccUpdateWLBadge() {
    const count = ccGetWL().length;
    document.querySelectorAll('.cc-wl-count').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? 'inline-flex' : 'none';
    });
    // Sync hearts state across all cards
    document.querySelectorAll('.cc-wl-heart').forEach(function (btn) {
      const h = btn.getAttribute('data-cc-wl-handle');
      if (!h) return;
      btn.classList.toggle('is-active', ccInWL(h));
      btn.setAttribute('aria-pressed', ccInWL(h) ? 'true' : 'false');
    });
  }
  function ccGetProductHandleFromCard(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }
  function ccInjectWishlistHearts() {
    const cards = document.querySelectorAll(
      '.card-wrapper, .card--product, .grid__item .card, .product-card-wrapper'
    );
    cards.forEach(function (card) {
      if (card.querySelector('.cc-wl-heart')) return;
      const handle = ccGetProductHandleFromCard(card);
      if (!handle) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cc-wl-heart';
      btn.setAttribute('data-cc-wl-handle', handle);
      btn.setAttribute('aria-label', ccT('أضف إلى المفضلة', 'Add to wishlist'));
      btn.setAttribute('aria-pressed', ccInWL(handle) ? 'true' : 'false');
      btn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
        '</svg>';
      if (ccInWL(handle)) btn.classList.add('is-active');
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        const added = ccToggleWL(handle);
        btn.classList.toggle('is-active', added);
        btn.setAttribute('aria-pressed', added ? 'true' : 'false');
        btn.classList.remove('cc-wl-bump');
        // eslint-disable-next-line no-unused-expressions
        btn.offsetWidth;
        btn.classList.add('cc-wl-bump');
      });
      const imgWrap = card.querySelector('.card__media, .media, .card__inner') || card;
      imgWrap.style.position = imgWrap.style.position || 'relative';
      imgWrap.appendChild(btn);
    });
  }

  function ccRenderWishlistPage() {
    if (!/\/pages\/wishlist/.test(location.pathname)) return;
    const host = document.querySelector('.page-width .rte, main .page-width, #MainContent .page-width');
    if (!host || host.querySelector('.cc-wl-page')) return;
    const wrap = document.createElement('div');
    wrap.className = 'cc-wl-page';
    wrap.innerHTML =
      '<div class="cc-wl-head">' +
        '<h1 class="cc-wl-title">' + ccT('قائمة المفضلة', 'My Wishlist') + '</h1>' +
        '<p class="cc-wl-sub">' + ccT('المنتجات التي حفظتها لاحقاً', 'Products you saved for later') + '</p>' +
      '</div>' +
      '<div class="cc-wl-grid" aria-live="polite"></div>' +
      '<div class="cc-wl-empty" hidden>' +
        '<div class="cc-wl-empty-icon">♡</div>' +
        '<p>' + ccT('قائمة المفضلة فارغة. أضف منتجات بالضغط على القلب.', 'Your wishlist is empty. Tap the heart on products to save them.') + '</p>' +
        '<a class="cc-wl-empty-cta" href="' + ccRootSlash() + 'collections/all">' +
          ccT('تصفح المنتجات', 'Browse Products') +
        '</a>' +
      '</div>';
    host.innerHTML = '';
    host.appendChild(wrap);
    const grid = wrap.querySelector('.cc-wl-grid');
    const empty = wrap.querySelector('.cc-wl-empty');
    const handles = ccGetWL();
    if (!handles.length) { empty.hidden = false; return; }
    handles.forEach(function (handle) {
      fetch(ccRootSlash() + 'products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return;
          const card = document.createElement('a');
          card.className = 'cc-wl-card';
          card.href = ccRootSlash() + 'products/' + p.handle;
          const img = p.featured_image ? p.featured_image : (p.images && p.images[0]) || '';
          const price = (p.price / 100).toFixed(3) + ' ' + ccT('د.ك', 'KWD');
          card.innerHTML =
            '<div class="cc-wl-card-media">' +
              (img ? '<img src="' + img + '" alt="' + (p.title || '') + '" loading="lazy">' : '') +
              '<button type="button" class="cc-wl-remove" aria-label="' + ccT('إزالة', 'Remove') + '" data-handle="' + p.handle + '">×</button>' +
            '</div>' +
            '<div class="cc-wl-card-body">' +
              '<div class="cc-wl-card-title">' + (p.title || '') + '</div>' +
              '<div class="cc-wl-card-price">' + price + '</div>' +
            '</div>';
          grid.appendChild(card);
          const rm = card.querySelector('.cc-wl-remove');
          rm.addEventListener('click', function (e) {
            e.preventDefault(); e.stopPropagation();
            const list = ccGetWL().filter(function (x) { return x !== handle; });
            ccSetWL(list);
            card.remove();
            if (!ccGetWL().length) empty.hidden = false;
          });
        })
        .catch(function () {});
    });
  }

  /* ───────── 57.2 PREDICTIVE SEARCH ───────── */
  function ccEnhanceSearch() {
    const forms = document.querySelectorAll('form[action*="/search"]');
    forms.forEach(function (form) {
      if (form.dataset.ccSearchEnhanced) return;
      form.dataset.ccSearchEnhanced = '1';
      const input = form.querySelector('input[type="search"], input[name="q"]');
      if (!input) return;
      const dropdown = document.createElement('div');
      dropdown.className = 'cc-search-dropdown';
      dropdown.hidden = true;
      form.style.position = form.style.position || 'relative';
      form.appendChild(dropdown);

      let lastReq = 0;
      function doSearch(q) {
        if (!q || q.length < 2) { dropdown.hidden = true; return; }
        const reqId = ++lastReq;
        const url = ccRootSlash() + 'search/suggest.json?q=' + encodeURIComponent(q) +
                    '&resources[type]=product,collection&resources[limit]=6';
        fetch(url)
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (reqId !== lastReq || !data) return;
            const prods = (data.resources && data.resources.results && data.resources.results.products) || [];
            const cols = (data.resources && data.resources.results && data.resources.results.collections) || [];
            if (!prods.length && !cols.length) { dropdown.hidden = true; return; }
            let html = '';
            if (prods.length) {
              html += '<div class="cc-sd-head">' + ccT('المنتجات', 'Products') + '</div>';
              prods.forEach(function (p) {
                html += '<a class="cc-sd-item" href="' + p.url + '">' +
                  (p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '<div class="cc-sd-ph"></div>') +
                  '<div class="cc-sd-info"><div class="cc-sd-title">' + p.title + '</div>' +
                  (p.price ? '<div class="cc-sd-price">' + p.price + '</div>' : '') +
                  '</div></a>';
              });
            }
            if (cols.length) {
              html += '<div class="cc-sd-head">' + ccT('التصنيفات', 'Collections') + '</div>';
              cols.forEach(function (c) {
                html += '<a class="cc-sd-item cc-sd-item--col" href="' + c.url + '">' +
                  '<div class="cc-sd-info"><div class="cc-sd-title">' + c.title + '</div></div></a>';
              });
            }
            html += '<a class="cc-sd-all" href="' + ccRootSlash() + 'search?q=' + encodeURIComponent(q) + '">' +
              ccT('عرض كل النتائج', 'See all results') + ' →</a>';
            dropdown.innerHTML = html;
            dropdown.hidden = false;
          })
          .catch(function () {});
      }
      let deb = null;
      input.addEventListener('input', function () {
        if (deb) clearTimeout(deb);
        deb = setTimeout(function () { doSearch(input.value.trim()); }, 180);
      });
      input.addEventListener('focus', function () {
        if (input.value.trim().length >= 2) doSearch(input.value.trim());
      });
      document.addEventListener('click', function (e) {
        if (!form.contains(e.target)) dropdown.hidden = true;
      });
    });
  }

  /* ───────── 57.3 PRODUCT REVIEW STARS ───────── */
  function ccInjectReviewStars() {
    if (!/\/products\//.test(location.pathname)) return;
    const titleEl = document.querySelector('.product__title, .product__info-wrapper h1');
    if (!titleEl || document.querySelector('.cc-reviews')) return;
    const container = titleEl.parentElement || titleEl;

    // Try to read metafields from a data attribute if merchant set them up
    const ratingEl = document.querySelector('[data-cc-rating]');
    const countEl = document.querySelector('[data-cc-rating-count]');
    const rating = ratingEl ? parseFloat(ratingEl.getAttribute('data-cc-rating')) : 0;
    const count = countEl ? parseInt(countEl.getAttribute('data-cc-rating-count'), 10) : 0;

    const reviews = document.createElement('div');
    reviews.className = 'cc-reviews';

    const starsHTML = (function (r) {
      let h = '<div class="cc-stars" aria-hidden="true">';
      for (let i = 1; i <= 5; i++) {
        const fill = r >= i ? 'full' : (r >= i - 0.5 ? 'half' : 'empty');
        h += '<span class="cc-star cc-star--' + fill + '">★</span>';
      }
      h += '</div>';
      return h;
    })(rating || 5);

    const igHref = 'https://instagram.com/' + CC_INSTAGRAM_HANDLE;

    if (count > 0) {
      reviews.innerHTML =
        starsHTML +
        '<span class="cc-reviews-meta">' + rating.toFixed(1) + ' / 5</span>' +
        '<span class="cc-reviews-count">(' + count + ' ' + ccT('تقييم', 'reviews') + ')</span>' +
        '<a class="cc-reviews-add" href="' + igHref + '" target="_blank" rel="noopener">' +
          ccT('شارك تجربتك', 'Share your experience') +
        '</a>';
    } else {
      reviews.innerHTML =
        starsHTML +
        '<span class="cc-reviews-meta">' + ccT('كن أول من يقيّم', 'Be the first to review') + '</span>' +
        '<a class="cc-reviews-add" href="' + igHref + '" target="_blank" rel="noopener">' +
          ccT('شارك تجربتك عبر انستقرام', 'Share on Instagram') +
        '</a>';
    }
    titleEl.insertAdjacentElement('afterend', reviews);
  }

  /* ───────── 57.4 BACK-IN-STOCK BUTTON ───────── */
  function ccInjectBackInStock() {
    if (!/\/products\//.test(location.pathname)) return;
    // Detect sold out state: look for disabled add-to-cart or "sold out" badge
    const btn = document.querySelector('[name="add"], .product-form__submit');
    if (!btn) return;
    if (document.querySelector('.cc-bis')) return;
    const isSoldOut =
      btn.disabled ||
      btn.hasAttribute('disabled') ||
      /sold out|نفد|نفذ/i.test((btn.textContent || '') + ' ' + (btn.getAttribute('aria-label') || ''));
    if (!isSoldOut) return;
    const igHref = 'https://instagram.com/' + CC_INSTAGRAM_HANDLE;
    const box = document.createElement('a');
    box.href = igHref;
    box.target = '_blank';
    box.rel = 'noopener';
    box.className = 'cc-bis';
    box.innerHTML =
      '<span class="cc-bis-icon">🔔</span>' +
      '<span class="cc-bis-text">' +
        '<strong>' + ccT('أشعرني عند التوفر', 'Notify me when available') + '</strong>' +
        '<small>' + ccT('تابعنا على انستقرام للإشعار', 'Follow us on Instagram for updates') + '</small>' +
      '</span>' +
      '<span class="cc-bis-arrow">→</span>';
    btn.insertAdjacentElement('afterend', box);
  }

  /* ───────── 57.5 SOCIAL BAR (Instagram / TikTok / X) ───────── */
  const CC_TIKTOK_HANDLE = 'mahsool.kw';
  const CC_TWITTER_HANDLE = 'mahsoolkw';
  function ccInjectSocialBar() {
    // Remove any legacy Instagram strip from earlier rounds
    document.querySelectorAll('.cc-ig-strip').forEach(function (el) { el.remove(); });

    const footer = document.querySelector('footer.footer, .footer');
    if (!footer || document.querySelector('.cc-social-bar')) return;

    const igHref = 'https://instagram.com/' + CC_INSTAGRAM_HANDLE;
    const ttHref = 'https://www.tiktok.com/@' + CC_TIKTOK_HANDLE;
    const xHref  = 'https://x.com/' + CC_TWITTER_HANDLE;

    const svgIG =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<rect x="3" y="3" width="18" height="18" rx="5"/>' +
      '<path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>' +
      '<line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>' +
      '</svg>';
    const svgTT =
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.3a8.16 8.16 0 0 0 4.77 1.52V6.38a4.85 4.85 0 0 1-1.84-.31 4.86 4.86 0 0 1 0 .62z"/>' +
      '</svg>';
    const svgX =
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>' +
      '</svg>';

    const bar = document.createElement('section');
    bar.className = 'cc-social-bar';
    bar.setAttribute('aria-label', ccT('تواصل معنا', 'Connect with us'));
    bar.innerHTML =
      '<div class="cc-social-inner">' +
        '<span class="cc-social-eyebrow">' + ccT('تواصل معنا', 'Follow Us') + '</span>' +
        '<h3 class="cc-social-title">' + ccT('محصول على السوشال ميديا', 'Mahsool on social') + '</h3>' +
        '<div class="cc-social-icons">' +
          '<a class="cc-social-icon cc-social-icon--ig" href="' + igHref + '" target="_blank" rel="noopener" aria-label="Instagram">' +
            svgIG + '<span>Instagram</span>' +
          '</a>' +
          '<a class="cc-social-icon cc-social-icon--tt" href="' + ttHref + '" target="_blank" rel="noopener" aria-label="TikTok">' +
            svgTT + '<span>TikTok</span>' +
          '</a>' +
          '<a class="cc-social-icon cc-social-icon--x" href="' + xHref + '" target="_blank" rel="noopener" aria-label="X">' +
            svgX + '<span>X</span>' +
          '</a>' +
        '</div>' +
      '</div>';
    footer.parentNode.insertBefore(bar, footer);
  }

  /* ───────── 57.6 ABOUT PAGE RENDER ───────── */
  function ccRenderAboutPage() {
    if (!/\/pages\/about/.test(location.pathname)) return;
    const host = document.querySelector('.page-width .rte, main .page-width, #MainContent .page-width');
    if (!host || host.querySelector('.cc-about')) return;
    const ar = ccIsAr();
    const wrap = document.createElement('div');
    wrap.className = 'cc-about';
    wrap.innerHTML =
      '<section class="cc-about-hero">' +
        '<span class="cc-about-eyebrow">' + (ar ? 'قصتنا' : 'OUR STORY') + '</span>' +
        '<h1 class="cc-about-title">' + (ar ? 'محصول' : 'Mahsool') + '</h1>' +
        '<p class="cc-about-tagline">' + (ar ? 'شغف القهوة المتخصصة في الكويت' : 'A passion for specialty coffee, crafted in Kuwait') + '</p>' +
      '</section>' +
      '<section class="cc-about-block">' +
        '<h2>' + (ar ? 'من نحن' : 'Who We Are') + '</h2>' +
        '<p>' + (ar ?
          'محصول هو وجهتك المتخصصة لكل ما يتعلق بالقهوة الفاخرة في الكويت. نختار بعناية أفضل الحبوب والمعدات والأدوات من حول العالم، لنقدم لعشاق القهوة تجربة استثنائية في كل فنجان.' :
          'Mahsool is your specialty destination for everything coffee in Kuwait. We carefully curate the finest beans, equipment, and tools from around the world to deliver an exceptional experience in every cup.'
        ) + '</p>' +
      '</section>' +
      '<section class="cc-about-values">' +
        '<div class="cc-about-value"><span class="cc-about-icon">✦</span><h3>' + (ar ? 'الجودة' : 'Quality') + '</h3><p>' +
          (ar ? 'نختار فقط أفضل المنتجات من موردين موثوقين عالميًا.' : 'We select only the best products from trusted global suppliers.') +
        '</p></div>' +
        '<div class="cc-about-value"><span class="cc-about-icon">❖</span><h3>' + (ar ? 'الخبرة' : 'Expertise') + '</h3><p>' +
          (ar ? 'فريقنا متخصص ومستعد لمساعدتك في اختيار ما يناسبك.' : 'Our specialist team is ready to help you choose what fits you best.') +
        '</p></div>' +
        '<div class="cc-about-value"><span class="cc-about-icon">✧</span><h3>' + (ar ? 'الشغف' : 'Passion') + '</h3><p>' +
          (ar ? 'نحن نحب القهوة بقدر ما تحبها، ونقدّم الأفضل دائماً.' : 'We love coffee as much as you do, and we always bring our best.') +
        '</p></div>' +
      '</section>' +
      '<section class="cc-about-cta">' +
        '<h2>' + (ar ? 'تواصل معنا' : 'Get in Touch') + '</h2>' +
        '<p>' + (ar ? 'أسئلة؟ توصيات؟ تابعونا على انستقرام' : 'Questions? Recommendations? Follow us on Instagram') + '</p>' +
        '<a class="cc-about-btn" href="https://instagram.com/' + CC_INSTAGRAM_HANDLE + '" target="_blank" rel="noopener">' +
          (ar ? 'تابعونا على انستقرام' : 'Follow on Instagram') +
        '</a>' +
      '</section>';
    host.innerHTML = '';
    host.appendChild(wrap);
  }

  /* ───────── 57.7 ORDER TRACKING PAGE ───────── */
  function ccRenderTrackingPage() {
    if (!/\/pages\/(order-tracking|tracking|track)/.test(location.pathname)) return;
    const host = document.querySelector('.page-width .rte, main .page-width, #MainContent .page-width');
    if (!host || host.querySelector('.cc-track')) return;
    const ar = ccIsAr();
    const wrap = document.createElement('div');
    wrap.className = 'cc-track';
    wrap.innerHTML =
      '<div class="cc-track-card">' +
        '<h1>' + (ar ? 'تتبع طلبك' : 'Track Your Order') + '</h1>' +
        '<p>' + (ar ? 'أدخل رقم طلبك وبريدك الإلكتروني لمعرفة حالة الشحن.' : 'Enter your order number and email to check shipping status.') + '</p>' +
        '<div class="cc-track-steps">' +
          '<div class="cc-track-step"><span>1</span><p>' + (ar ? 'سجّل الدخول لحسابك' : 'Log in to your account') + '</p></div>' +
          '<div class="cc-track-step"><span>2</span><p>' + (ar ? 'اذهب إلى طلباتي' : 'Go to My Orders') + '</p></div>' +
          '<div class="cc-track-step"><span>3</span><p>' + (ar ? 'اضغط على الطلب لعرض التفاصيل' : 'Click the order for details') + '</p></div>' +
        '</div>' +
        '<a class="cc-track-btn" href="' + ccRootSlash() + 'account/login">' +
          (ar ? 'تسجيل الدخول لطلباتي' : 'Log in to My Orders') +
        '</a>' +
        '<div class="cc-track-or">' + (ar ? 'أو' : 'OR') + '</div>' +
        '<a class="cc-track-wa" href="https://instagram.com/' + CC_INSTAGRAM_HANDLE + '" target="_blank" rel="noopener">' +
          (ar ? 'استفسر عن طلبك عبر انستقرام' : 'Ask about your order on Instagram') +
        '</a>' +
      '</div>';
    host.innerHTML = '';
    host.appendChild(wrap);
  }

  /* ───────── 57.8 SHIPPING POLICY PAGE ───────── */
  function ccRenderShippingPage() {
    if (!/\/pages\/(shipping|shipping-delivery|shipping-policy|delivery)/.test(location.pathname)) return;
    const host = document.querySelector('.page-width .rte, main .page-width, #MainContent .page-width');
    if (!host || host.querySelector('.cc-ship')) return;
    const ar = ccIsAr();
    const wrap = document.createElement('div');
    wrap.className = 'cc-ship';
    wrap.innerHTML =
      '<h1 class="cc-ship-title">' + (ar ? 'الشحن والتوصيل' : 'Shipping & Delivery') + '</h1>' +
      '<p class="cc-ship-sub">' + (ar ? 'كل ما تحتاج معرفته عن عملية التوصيل' : 'Everything you need to know about delivery') + '</p>' +
      '<div class="cc-ship-grid">' +
        '<div class="cc-ship-card"><span class="cc-ship-icon">⚡</span><h3>' +
          (ar ? 'توصيل سريع' : 'Fast Delivery') + '</h3><p>' +
          (ar ? 'توصيل خلال 24–48 ساعة داخل الكويت للطلبات المتوفرة.' : 'Delivery within 24–48 hours inside Kuwait for in-stock items.') +
        '</p></div>' +
        '<div class="cc-ship-card"><span class="cc-ship-icon">📦</span><h3>' +
          (ar ? 'تغليف فاخر' : 'Premium Packaging') + '</h3><p>' +
          (ar ? 'جميع الطلبات تُغلّف بعناية لضمان وصولها بحالة مثالية.' : 'Every order is carefully packaged to arrive in perfect condition.') +
        '</p></div>' +
        '<div class="cc-ship-card"><span class="cc-ship-icon">🌍</span><h3>' +
          (ar ? 'تغطية كاملة' : 'Full Coverage') + '</h3><p>' +
          (ar ? 'نوصل إلى جميع مناطق الكويت.' : 'We deliver to all governorates in Kuwait.') +
        '</p></div>' +
        '<div class="cc-ship-card"><span class="cc-ship-icon">💬</span><h3>' +
          (ar ? 'متابعة مستمرة' : 'Constant Updates') + '</h3><p>' +
          (ar ? 'تابعنا على انستقرام لمتابعة حالة طلبك.' : 'Follow us on Instagram for order updates.') +
        '</p></div>' +
      '</div>' +
      '<div class="cc-ship-note">' +
        (ar ? 'للاستفسارات حول الشحن، تابعونا على' : 'For shipping inquiries, follow us on') +
        ' <a href="https://instagram.com/' + CC_INSTAGRAM_HANDLE + '" target="_blank" rel="noopener">' +
          (ar ? 'انستقرام' : 'Instagram') +
        '</a>' +
      '</div>';
    host.innerHTML = '';
    host.appendChild(wrap);
  }

  /* ───────── 57.9 HEADER WISHLIST LINK ───────── */
  function ccInjectHeaderWishlist() {
    const icons = document.querySelector('.header__icons');
    if (!icons || icons.querySelector('.cc-wl-header')) return;
    const a = document.createElement('a');
    a.className = 'cc-wl-header header__icon link focus-inset';
    a.href = ccRootSlash() + 'pages/wishlist';
    a.setAttribute('aria-label', ccT('قائمة المفضلة', 'Wishlist'));
    a.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="22" height="22">' +
      '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
      '</svg>' +
      '<span class="cc-wl-count" style="display:none">0</span>';
    // Insert before cart icon if exists
    const cart = icons.querySelector('.header__icon--cart, a[href*="/cart"]');
    if (cart) icons.insertBefore(a, cart);
    else icons.appendChild(a);
    ccUpdateWLBadge();
  }

  /* ───────── 58. Newsletter form polish ───────── */
  function ccPolishNewsletter() {
    const ar = ccIsAr();
    // Fix heading: "Stay Updated | ابق على اطلاع" → one language
    document.querySelectorAll('.footer-block--newsletter .footer-block__heading, .footer-block__newsletter .footer-block__heading').forEach(function (h) {
      const txt = (h.textContent || '').trim();
      if (txt.indexOf('|') !== -1) {
        const parts = txt.split('|').map(function (s) { return s.trim(); });
        // Detect which part is Arabic
        const arPart = parts.find(function (p) { return /[\u0600-\u06FF]/.test(p); }) || parts[1];
        const enPart = parts.find(function (p) { return !/[\u0600-\u06FF]/.test(p); }) || parts[0];
        h.textContent = ar ? (arPart || 'ابق على اطلاع') : (enPart || 'Stay Updated');
      }
    });
    // Localize placeholder and add tagline
    document.querySelectorAll('.footer__newsletter .field__input, .footer-block__newsletter input[type="email"]').forEach(function (input) {
      input.setAttribute('placeholder', ar ? 'بريدك الإلكتروني' : 'Your email address');
    });
    // Localize submit aria-label
    document.querySelectorAll('.footer__newsletter .newsletter-form__button, .footer__newsletter .field__button').forEach(function (btn) {
      btn.setAttribute('aria-label', ar ? 'اشترك' : 'Subscribe');
      btn.setAttribute('title', ar ? 'اشترك' : 'Subscribe');
    });
    // Add subtle tagline under form once
    document.querySelectorAll('.footer-block__newsletter').forEach(function (wrap) {
      if (wrap.querySelector('.cc-nl-tagline')) return;
      const tag = document.createElement('span');
      tag.className = 'cc-nl-tagline';
      tag.textContent = ar
        ? 'أحدث المنتجات والعروض الحصرية'
        : 'Latest products & exclusive offers';
      wrap.appendChild(tag);
    });
  }

  // ========================================================================
  // ROUND 60 — Premium UX Layer (10 features)
  // ========================================================================
  const CC_FREE_SHIP_THRESHOLD = 15; // KWD
  const CC_RV_KEY = 'cc_recently_viewed';
  const CC_RV_MAX = 12;

  // -------- 1. Sticky Mobile Add-to-Cart --------
  function ccInjectStickyAtc() {
    const exist = document.querySelector('.cc-sticky-atc');
    if (window.innerWidth > 749 || !document.body.classList.contains('template-product')) {
      if (exist) exist.remove();
      return;
    }
    if (exist) return;
    const form = document.querySelector('main form[action*="/cart/add"]');
    if (!form) return;
    const mainBtn = form.querySelector('[name="add"]');
    if (!mainBtn) return;
    const titleEl = document.querySelector('h1.product__title, .product__title h1, .product__title');
    const title = titleEl ? titleEl.textContent.trim() : '';
    const priceEl = document.querySelector('.price__container .price-item--sale:not([hidden]), .price__container .price-item--regular, .price-item--sale, .price-item--regular');
    const price = priceEl ? priceEl.textContent.trim() : '';
    const imgEl = document.querySelector('.product__media img, product-media img, media-gallery img');
    const imgSrc = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';
    const ar = ccIsAr();
    const bar = document.createElement('div');
    bar.className = 'cc-sticky-atc';
    bar.innerHTML =
      '<div class="cc-sticky-atc__inner">' +
        (imgSrc ? '<img class="cc-sticky-atc__img" src="' + imgSrc + '" alt="" loading="lazy">' : '') +
        '<div class="cc-sticky-atc__info">' +
          '<div class="cc-sticky-atc__title">' + title + '</div>' +
          (price ? '<div class="cc-sticky-atc__price">' + price + '</div>' : '') +
        '</div>' +
        '<button type="button" class="cc-sticky-atc__btn">' + (ar ? 'أضف للسلة' : 'Add to cart') + '</button>' +
      '</div>';
    document.body.appendChild(bar);
    bar.querySelector('.cc-sticky-atc__btn').addEventListener('click', function () {
      const submit = form.querySelector('[name="add"]');
      if (submit) {
        submit.click();
        try { form.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
      }
    });
    try {
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          bar.classList.toggle('cc-sticky-atc--visible', !e.isIntersecting);
        });
      }, { threshold: 0.1 });
      io.observe(mainBtn);
    } catch (_) {
      bar.classList.add('cc-sticky-atc--visible');
    }
  }

  // -------- 2. Free Shipping Progress Bar --------
  function ccCartDivisor(currency) {
    const c = (currency || '').toUpperCase();
    return (c === 'KWD' || c === 'BHD' || c === 'OMR' || c === 'JOD') ? 1000 : 100;
  }

  function ccFetchCartAndUpdateShipBar() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const div = ccCartDivisor(cart.currency);
        const total = (cart.total_price || 0) / div;
        ccUpdateShipBar(total);
      })
      .catch(function () {});
  }

  function ccUpdateShipBar(total) {
    const ar = ccIsAr();
    const remain = Math.max(CC_FREE_SHIP_THRESHOLD - total, 0);
    const pct = Math.min((total / CC_FREE_SHIP_THRESHOLD) * 100, 100);
    const txt = remain === 0
      ? (ar ? '🎉 حصلت على الشحن المجاني!' : '🎉 You got free shipping!')
      : (ar
          ? 'أضف ' + remain.toFixed(3) + ' د.ك للحصول على شحن مجاني'
          : 'Add ' + remain.toFixed(3) + ' KWD for free shipping');
    document.querySelectorAll('.cc-ship-bar').forEach(function (bar) {
      const tEl = bar.querySelector('.cc-ship-bar__text');
      const fEl = bar.querySelector('.cc-ship-bar__fill');
      if (tEl) tEl.textContent = txt;
      if (fEl) fEl.style.width = pct + '%';
      bar.classList.toggle('cc-ship-bar--complete', remain === 0);
    });
  }

  function ccInjectShipBarElement(container, extraClass) {
    if (!container || container.querySelector(':scope > .cc-ship-bar')) return null;
    const bar = document.createElement('div');
    bar.className = 'cc-ship-bar' + (extraClass ? ' ' + extraClass : '');
    bar.innerHTML =
      '<div class="cc-ship-bar__text"></div>' +
      '<div class="cc-ship-bar__track"><div class="cc-ship-bar__fill"></div></div>';
    container.prepend(bar);
    return bar;
  }

  function ccInjectShipProgress() {
    // Cart drawer
    const drawer = document.querySelector('cart-drawer, #CartDrawer');
    if (drawer && !drawer.dataset.ccShipWired) {
      drawer.dataset.ccShipWired = '1';
      const target = drawer.querySelector('.drawer__inner-empty') ? null : (drawer.querySelector('.drawer__inner, cart-drawer-items, .drawer__contents') || drawer);
      if (target) ccInjectShipBarElement(target, 'cc-ship-bar--drawer');
      let t = null;
      const mo = new MutationObserver(function () {
        if (t) clearTimeout(t);
        t = setTimeout(function () {
          const inner = drawer.querySelector('.drawer__inner, cart-drawer-items, .drawer__contents');
          if (inner && !inner.querySelector(':scope > .cc-ship-bar')) {
            ccInjectShipBarElement(inner, 'cc-ship-bar--drawer');
          }
          ccFetchCartAndUpdateShipBar();
        }, 180);
      });
      mo.observe(drawer, { childList: true, subtree: true });
      ccFetchCartAndUpdateShipBar();
    }
    // Cart page
    if (document.body.classList.contains('template-cart')) {
      const pageTarget = document.querySelector('cart-items, .cart__items, main .cart');
      if (pageTarget && !document.querySelector('.cc-ship-bar--page')) {
        const wrap = document.createElement('div');
        wrap.style.maxWidth = '920px';
        wrap.style.margin = '0 auto';
        pageTarget.parentNode.insertBefore(wrap, pageTarget);
        ccInjectShipBarElement(wrap, 'cc-ship-bar--page');
        ccFetchCartAndUpdateShipBar();
      }
    }
  }

  // -------- 3. Recently Viewed --------
  function ccGetRV() {
    try { return JSON.parse(localStorage.getItem(CC_RV_KEY) || '[]'); } catch (_) { return []; }
  }
  function ccSetRV(list) {
    try { localStorage.setItem(CC_RV_KEY, JSON.stringify(list)); } catch (_) {}
  }

  function ccTrackRecentlyViewed() {
    if (!document.body.classList.contains('template-product')) return;
    const canon = document.querySelector('link[rel="canonical"]');
    const url = canon ? canon.href : window.location.href;
    const match = url.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const handle = match[1];
    const titleEl = document.querySelector('h1.product__title, .product__title h1, .product__title');
    const imgEl = document.querySelector('.product__media img, product-media img, media-gallery img');
    const priceEl = document.querySelector('.price__container .price-item--sale:not([hidden]), .price__container .price-item--regular');
    const item = {
      handle: handle,
      title: titleEl ? titleEl.textContent.trim() : handle,
      img: imgEl ? (imgEl.currentSrc || imgEl.src || '') : '',
      price: priceEl ? priceEl.textContent.trim() : '',
      ts: Date.now()
    };
    let list = ccGetRV().filter(function (x) { return x.handle !== handle; });
    list.unshift(item);
    list = list.slice(0, CC_RV_MAX);
    ccSetRV(list);
  }

  function ccRenderRecentlyViewed() {
    const isProduct = document.body.classList.contains('template-product');
    const isHome = document.body.classList.contains('template-index');
    if (!isProduct && !isHome) return;
    if (document.querySelector('.cc-rv')) return;
    let list = ccGetRV();
    if (isProduct) {
      const canon = document.querySelector('link[rel="canonical"]');
      const url = canon ? canon.href : window.location.href;
      const m = url.match(/\/products\/([^/?#]+)/);
      const cur = m ? m[1] : null;
      list = list.filter(function (x) { return x.handle !== cur; });
    }
    if (list.length < 2) return;
    const ar = ccIsAr();
    const root = ccRootSlash();
    const sec = document.createElement('section');
    sec.className = 'cc-rv';
    sec.innerHTML =
      '<div class="cc-rv__inner">' +
        '<h2 class="cc-rv__title">' + (ar ? 'شاهدت مؤخراً' : 'Recently Viewed') + '</h2>' +
        '<div class="cc-rv__grid">' +
          list.slice(0, 8).map(function (x) {
            return '<a class="cc-rv__card" href="' + root + 'products/' + x.handle + '">' +
              '<div class="cc-rv__img">' + (x.img ? '<img src="' + x.img + '" alt="" loading="lazy">' : '') + '</div>' +
              '<div class="cc-rv__info">' +
                '<div class="cc-rv__name">' + x.title + '</div>' +
                (x.price ? '<div class="cc-rv__price">' + x.price + '</div>' : '') +
              '</div>' +
            '</a>';
          }).join('') +
        '</div>' +
      '</div>';
    const main = document.querySelector('main#MainContent, main') || document.body;
    main.appendChild(sec);
  }

  // -------- 4. Trust Badges --------
  function ccInjectTrustBadges() {
    if (!document.body.classList.contains('template-product')) return;
    if (document.querySelector('.cc-trust-row')) return;
    const form = document.querySelector('main form[action*="/cart/add"]');
    if (!form) return;
    const ar = ccIsAr();
    const items = ar ? [
      { k: 'lock', l: 'دفع آمن' },
      { k: 'truck', l: 'توصيل الكويت 24 ساعة' },
      { k: 'shield', l: 'منتجات أصلية 100%' }
    ] : [
      { k: 'lock', l: 'Secure payment' },
      { k: 'truck', l: '24h Kuwait delivery' },
      { k: 'shield', l: '100% authentic' }
    ];
    const svgs = {
      lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>',
      truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v10H3zM14 10h4l3 3v4h-7z"/><circle cx="7.5" cy="18" r="2"/><circle cx="17.5" cy="18" r="2"/></svg>',
      shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6z"/><path d="M9 12l2 2 4-4"/></svg>'
    };
    const row = document.createElement('div');
    row.className = 'cc-trust-row';
    row.innerHTML = items.map(function (it) {
      return '<div class="cc-trust-item">' +
        '<span class="cc-trust-icon">' + svgs[it.k] + '</span>' +
        '<span class="cc-trust-label">' + it.l + '</span>' +
      '</div>';
    }).join('');
    const afterTarget = form.querySelector('.product-form__buttons') || form;
    if (afterTarget.parentNode) {
      afterTarget.parentNode.insertBefore(row, afterTarget.nextSibling);
    }
  }

  // -------- 5. Breadcrumbs --------
  function ccInjectBreadcrumbs() {
    if (document.querySelector('.cc-crumbs')) return;
    const isProduct = document.body.classList.contains('template-product');
    const isCollection = document.body.classList.contains('template-collection');
    if (!isProduct && !isCollection) return;
    const ar = ccIsAr();
    const root = ccRootSlash();
    const crumbs = [{ label: ar ? 'الرئيسية' : 'Home', href: root }];
    if (isCollection) {
      const h1 = document.querySelector('.collection-hero__title, .title--primary, main h1');
      if (h1) crumbs.push({ label: h1.textContent.trim(), href: '' });
    } else if (isProduct) {
      const h1 = document.querySelector('h1.product__title, .product__title h1, .product__title');
      crumbs.push({ label: ar ? 'المنتجات' : 'Products', href: root + 'collections/all' });
      if (h1) crumbs.push({ label: h1.textContent.trim(), href: '' });
    }
    const nav = document.createElement('nav');
    nav.className = 'cc-crumbs';
    nav.setAttribute('aria-label', 'Breadcrumb');
    nav.innerHTML = '<div class="cc-crumbs__inner">' + crumbs.map(function (c, i) {
      const isLast = i === crumbs.length - 1;
      const el = (c.href && !isLast)
        ? '<a href="' + c.href + '">' + c.label + '</a>'
        : '<span>' + c.label + '</span>';
      return el + (isLast ? '' : '<span class="cc-crumbs__sep">›</span>');
    }).join('') + '</div>';
    const main = document.querySelector('main#MainContent, main');
    if (main && main.firstChild) main.insertBefore(nav, main.firstChild);
  }

  // -------- 6. Empty States --------
  function ccEnhanceEmptyStates() {
    // empty cart
    if (document.body.classList.contains('template-cart')) {
      const empty = document.querySelector('.cart__warnings, .cart--empty, .is-empty .cart__warnings');
      if (empty && !empty.querySelector('.cc-empty')) {
        const ar = ccIsAr();
        const root = ccRootSlash();
        const box = document.createElement('div');
        box.className = 'cc-empty cc-empty--cart';
        box.innerHTML =
          '<div class="cc-empty__icon"><svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="26" cy="54" r="3"/><circle cx="50" cy="54" r="3"/><path d="M8 12h8l6 32h32l6-20H22"/></svg></div>' +
          '<h2 class="cc-empty__title">' + (ar ? 'سلتك فارغة' : 'Your cart is empty') + '</h2>' +
          '<p class="cc-empty__text">' + (ar ? 'اكتشف أفضل معدات القهوة وابدأ التسوق' : 'Discover premium coffee gear and start shopping') + '</p>' +
          '<a class="cc-empty__btn" href="' + root + 'collections/all">' + (ar ? 'تسوق الآن' : 'Shop now') + '</a>';
        empty.prepend(box);
      }
    }
    // 404
    if (document.body.classList.contains('template-404')) {
      const main = document.querySelector('main#MainContent, main');
      if (main && !main.querySelector('.cc-empty--404')) {
        const ar = ccIsAr();
        const root = ccRootSlash();
        const box = document.createElement('div');
        box.className = 'cc-empty cc-empty--404';
        box.innerHTML =
          '<div class="cc-empty__icon cc-empty__icon--404">404</div>' +
          '<h2 class="cc-empty__title">' + (ar ? 'الصفحة غير موجودة' : 'Page not found') + '</h2>' +
          '<p class="cc-empty__text">' + (ar ? 'يبدو أن الصفحة اللي تبحث عنها انتقلت أو غير موجودة' : 'The page you are looking for was moved or removed') + '</p>' +
          '<a class="cc-empty__btn" href="' + root + '">' + (ar ? 'العودة للرئيسية' : 'Back to home') + '</a>';
        const existing = main.querySelector('.title-wrapper, .center, .page-width');
        if (existing) existing.innerHTML = '';
        main.prepend(box);
      }
    }
  }

  // -------- 7. Scroll to Top --------
  function ccInjectScrollTop() {
    if (document.querySelector('.cc-scroll-top')) return;
    const ar = ccIsAr();
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cc-scroll-top';
    btn.setAttribute('aria-label', ar ? 'العودة للأعلى' : 'Scroll to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M6 11l6-6 6 6"/></svg>';
    document.body.appendChild(btn);
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    const onScroll = function () {
      btn.classList.toggle('cc-scroll-top--visible', window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // -------- 9. Active Filter Chips --------
  function ccInjectFilterChips() {
    if (!document.body.classList.contains('template-collection')) return;
    const existing = document.querySelector('.cc-chips');
    const params = new URLSearchParams(window.location.search);
    const filters = [];
    params.forEach(function (val, key) {
      if (key.indexOf('filter.') === 0) filters.push({ key: key, val: val });
    });
    if (!filters.length) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;
    const ar = ccIsAr();
    const bar = document.createElement('div');
    bar.className = 'cc-chips';
    bar.innerHTML = '<div class="cc-chips__inner">' +
      filters.map(function (f) {
        return '<button type="button" class="cc-chip" data-key="' + f.key + '" data-val="' + f.val.replace(/"/g, '&quot;') + '">' +
          '<span>' + f.val + '</span>' +
          '<span class="cc-chip__x">×</span>' +
        '</button>';
      }).join('') +
      '<button type="button" class="cc-chip cc-chip--clear">' + (ar ? 'مسح الكل' : 'Clear all') + '</button>' +
      '</div>';
    const target = document.querySelector('#ProductGridContainer, .collection, main');
    if (target && target.parentNode) target.parentNode.insertBefore(bar, target);
    bar.querySelectorAll('.cc-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.classList.contains('cc-chip--clear')) {
          window.location.href = window.location.pathname;
          return;
        }
        const newParams = new URLSearchParams(window.location.search);
        const key = chip.dataset.key;
        const val = chip.dataset.val;
        const vals = newParams.getAll(key).filter(function (v) { return v !== val; });
        newParams.delete(key);
        vals.forEach(function (v) { newParams.append(key, v); });
        const qs = newParams.toString();
        window.location.search = qs ? '?' + qs : '';
      });
    });
  }

  // -------- 10. Image Zoom / Lightbox --------
  function ccInjectImageZoom() {
    if (!document.body.classList.contains('template-product')) return;
    const imgs = document.querySelectorAll('.product__media img, product-media img, media-gallery img');
    imgs.forEach(function (img) {
      if (img.dataset.ccZoomWired) return;
      img.dataset.ccZoomWired = '1';
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const src = img.currentSrc || img.src || '';
        const hiSrc = src.replace(/_(\d+x\d*|\d+x|small|medium|large|grande|master)\./, '_2048x.');
        ccOpenLightbox(hiSrc || src, img.alt || '');
      });
    });
  }

  function ccOpenLightbox(src, alt) {
    const existing = document.querySelector('.cc-lightbox');
    if (existing) existing.remove();
    const box = document.createElement('div');
    box.className = 'cc-lightbox';
    box.innerHTML =
      '<button type="button" class="cc-lightbox__close" aria-label="Close">×</button>' +
      '<div class="cc-lightbox__stage"><img src="' + src + '" alt="' + (alt || '').replace(/"/g, '&quot;') + '"></div>';
    document.body.appendChild(box);
    document.body.classList.add('cc-lightbox-open');
    requestAnimationFrame(function () { box.classList.add('cc-lightbox--visible'); });
    const close = function () {
      box.classList.remove('cc-lightbox--visible');
      document.body.classList.remove('cc-lightbox-open');
      setTimeout(function () { if (box.parentNode) box.remove(); }, 250);
    };
    box.querySelector('.cc-lightbox__close').addEventListener('click', close);
    box.addEventListener('click', function (e) {
      if (e.target === box || e.target.classList.contains('cc-lightbox__stage')) close();
    });
    const onEsc = function (e) {
      if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
    };
    document.addEventListener('keydown', onEsc);
  }

  // ========================================================================
  // ROUND 61 — Conversion Boosters (6 features)
  // ========================================================================

  // -------- 1. Toast Notifications --------
  function ccInjectToast() {
    if (document.querySelector('.cc-toast-container')) return;
    var container = document.createElement('div');
    container.className = 'cc-toast-container';
    document.body.appendChild(container);
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
      var result = origFetch.apply(this, arguments);
      if (urlStr.indexOf('/cart/add') !== -1) {
        result.then(function (resp) {
          if (resp.ok) {
            var ar = ccIsAr();
            ccShowToast(ar ? '✓ تمت الإضافة للسلة' : '✓ Added to cart', 'success');
          }
        }).catch(function () {});
      }
      return result;
    };
  }
  function ccShowToast(msg, type) {
    var container = document.querySelector('.cc-toast-container');
    if (!container) return;
    var toast = document.createElement('div');
    toast.className = 'cc-toast cc-toast--' + (type || 'info');
    toast.textContent = msg;
    container.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('cc-toast--visible'); });
    setTimeout(function () {
      toast.classList.remove('cc-toast--visible');
      setTimeout(function () { if (toast.parentNode) toast.remove(); }, 350);
    }, 3000);
  }

  // -------- 2. Product Badges (NEW / Sale %) --------
  function ccInjectProductBadges() {
    var cards = document.querySelectorAll('.product-grid .card-wrapper, .collection .card-wrapper, ul.grid .card-wrapper, .related-products .card-wrapper');
    var thirtyDays = 30 * 24 * 60 * 60 * 1000;
    var ar = ccIsAr();
    cards.forEach(function (card) {
      if (card.dataset.ccBadged) return;
      card.dataset.ccBadged = '1';
      var badges = [];
      // Sale badge
      var priceWrap = card.querySelector('.price--on-sale');
      if (priceWrap) {
        var compareEl = card.querySelector('s.price-item, .price-item--regular.price-item--last');
        var saleEl = card.querySelector('.price-item--sale');
        if (!saleEl) saleEl = card.querySelector('.price-item--regular:not(s):not(.price-item--last)');
        if (compareEl && saleEl) {
          var cmp = parseFloat(compareEl.textContent.replace(/[^\d.]/g, ''));
          var sal = parseFloat(saleEl.textContent.replace(/[^\d.]/g, ''));
          if (cmp > 0 && sal > 0 && cmp > sal) {
            var pct = Math.round((1 - sal / cmp) * 100);
            if (pct > 0 && pct < 90) badges.push({ text: '-' + pct + '%', cls: 'sale' });
          }
        }
      }
      // NEW badge
      var created = card.getAttribute('data-created');
      if (created) {
        var ts = parseInt(created, 10) * 1000;
        if (ts > 0 && (Date.now() - ts) < thirtyDays) {
          badges.push({ text: ar ? 'جديد' : 'NEW', cls: 'new' });
        }
      }
      if (!badges.length) return;
      var target = card.querySelector('.card__inner') || card.querySelector('.card');
      if (!target) return;
      target.style.position = 'relative';
      var wrap = document.createElement('div');
      wrap.className = 'cc-badges';
      wrap.innerHTML = badges.map(function (b) {
        return '<span class="cc-badge cc-badge--' + b.cls + '">' + b.text + '</span>';
      }).join('');
      target.appendChild(wrap);
    });
  }

  // -------- 3. Low Stock Counter --------
  function ccInjectLowStock() {
    if (!document.body.classList.contains('template-product')) return;
    if (document.querySelector('.cc-low-stock')) return;
    var canon = document.querySelector('link[rel="canonical"]');
    var url = canon ? canon.href : window.location.href;
    var match = url.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    fetch('/products/' + match[1] + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var variant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
        if (!variant) return;
        var qty = variant.inventory_quantity;
        if (qty <= 0 || qty > 10) return;
        var ar = ccIsAr();
        var el = document.createElement('div');
        el.className = 'cc-low-stock';
        el.innerHTML = '<span class="cc-low-stock__dot"></span>' +
          (ar ? 'باقي ' + qty + ' قطع فقط!' : 'Only ' + qty + ' left in stock!');
        var priceEl = document.querySelector('.product__info-container .price, .product__info-wrapper .price, product-info .price, .product .price');
        if (priceEl && priceEl.parentNode) priceEl.parentNode.insertBefore(el, priceEl.nextSibling);
      })
      .catch(function () {});
  }

  // -------- 4. Quick View --------
  function ccInjectQuickViewButtons() {
    var cards = document.querySelectorAll('.product-grid .card-wrapper, .collection .card-wrapper, ul.grid .card-wrapper');
    cards.forEach(function (card) {
      if (card.dataset.ccQvWired) return;
      card.dataset.ccQvWired = '1';
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      var href = link.getAttribute('href');
      var match = href.match(/\/products\/([^/?#]+)/);
      if (!match) return;
      var handle = match[1];
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cc-qv-btn';
      btn.setAttribute('aria-label', ccIsAr() ? 'نظرة سريعة' : 'Quick view');
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
      var target = card.querySelector('.card__inner') || card.querySelector('.card');
      if (target) {
        target.style.position = 'relative';
        target.appendChild(btn);
      }
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        ccOpenQuickView(handle);
      });
    });
  }
  function ccOpenQuickView(handle) {
    var existing = document.querySelector('.cc-qv-modal');
    if (existing) existing.remove();
    var ar = ccIsAr();
    var root = ccRootSlash();
    var modal = document.createElement('div');
    modal.className = 'cc-qv-modal';
    modal.innerHTML =
      '<div class="cc-qv-modal__overlay"></div>' +
      '<div class="cc-qv-modal__content">' +
        '<button type="button" class="cc-qv-modal__close" aria-label="Close">×</button>' +
        '<div class="cc-qv-modal__body cc-qv-modal__body--loading"><div class="cc-qv-loader"></div></div>' +
      '</div>';
    document.body.appendChild(modal);
    document.body.classList.add('cc-qv-open');
    requestAnimationFrame(function () { modal.classList.add('cc-qv-modal--visible'); });
    var closeModal = function () {
      modal.classList.remove('cc-qv-modal--visible');
      document.body.classList.remove('cc-qv-open');
      setTimeout(function () { if (modal.parentNode) modal.remove(); }, 300);
    };
    modal.querySelector('.cc-qv-modal__close').addEventListener('click', closeModal);
    modal.querySelector('.cc-qv-modal__overlay').addEventListener('click', closeModal);
    var onEsc = function (e) { if (e.key === 'Escape') { closeModal(); document.removeEventListener('keydown', onEsc); } };
    document.addEventListener('keydown', onEsc);
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var v = product.variants.find(function (x) { return x.available; }) || product.variants[0];
        var images = (product.images || []).slice(0, 5);
        var div = 1000;
        var price = v ? (v.price / div).toFixed(3) : '';
        var cmpPrice = (v && v.compare_at_price) ? (v.compare_at_price / div).toFixed(3) : '';
        var cur = ar ? ' د.ك' : ' KWD';
        var body = modal.querySelector('.cc-qv-modal__body');
        body.classList.remove('cc-qv-modal__body--loading');
        body.innerHTML =
          '<div class="cc-qv__images">' +
            '<div class="cc-qv__main-img">' + (images[0] ? '<img src="' + images[0] + '" alt="">' : '') + '</div>' +
            (images.length > 1 ? '<div class="cc-qv__thumbs">' + images.map(function (img, i) {
              return '<img src="' + img + '" alt="" class="cc-qv__thumb' + (i === 0 ? ' cc-qv__thumb--active' : '') + '">';
            }).join('') + '</div>' : '') +
          '</div>' +
          '<div class="cc-qv__info">' +
            '<h2 class="cc-qv__title">' + product.title + '</h2>' +
            (product.vendor ? '<div class="cc-qv__vendor">' + product.vendor + '</div>' : '') +
            '<div class="cc-qv__price">' +
              (cmpPrice ? '<s class="cc-qv__compare">' + cmpPrice + cur + '</s> ' : '') +
              '<span class="cc-qv__sale">' + price + cur + '</span>' +
            '</div>' +
            (product.description ? '<div class="cc-qv__desc">' + product.description.replace(/<[^>]+>/g, ' ').substring(0, 180) + '…</div>' : '') +
            '<div class="cc-qv__actions">' +
              (v && v.available
                ? '<button type="button" class="cc-qv__atc" data-vid="' + v.id + '">' + (ar ? 'أضف للسلة' : 'Add to cart') + '</button>'
                : '<button type="button" class="cc-qv__atc cc-qv__atc--sold" disabled>' + (ar ? 'نفذ من المخزون' : 'Sold out') + '</button>') +
              '<a class="cc-qv__view" href="' + root + 'products/' + handle + '">' + (ar ? 'عرض التفاصيل' : 'View details') + '</a>' +
            '</div>' +
          '</div>';
        body.querySelectorAll('.cc-qv__thumb').forEach(function (th) {
          th.addEventListener('click', function () {
            body.querySelectorAll('.cc-qv__thumb').forEach(function (t) { t.classList.remove('cc-qv__thumb--active'); });
            th.classList.add('cc-qv__thumb--active');
            var main = body.querySelector('.cc-qv__main-img img');
            if (main) main.src = th.src;
          });
        });
        var atcBtn = body.querySelector('.cc-qv__atc:not(.cc-qv__atc--sold)');
        if (atcBtn) {
          atcBtn.addEventListener('click', function () {
            atcBtn.textContent = ar ? 'جاري…' : 'Adding…';
            atcBtn.disabled = true;
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: parseInt(atcBtn.dataset.vid), quantity: 1 }] })
            }).then(function (r) {
              if (r.ok) { atcBtn.textContent = ar ? '✓ تمت الإضافة' : '✓ Added'; setTimeout(closeModal, 1200); }
              else { atcBtn.textContent = ar ? 'خطأ' : 'Error'; atcBtn.disabled = false; }
            }).catch(function () { atcBtn.textContent = ar ? 'خطأ' : 'Error'; atcBtn.disabled = false; });
          });
        }
      })
      .catch(function () {
        modal.querySelector('.cc-qv-modal__body').innerHTML = '<p style="text-align:center;color:#fff;padding:40px">' + (ar ? 'خطأ في التحميل' : 'Failed to load') + '</p>';
      });
  }

  // -------- 5. FAQ Accordion --------
  function ccRenderFaqPage() {
    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('/pages/faq') === -1 && path.indexOf('/pages/الاسئلة') === -1 && path.indexOf('/pages/%d8%a7%d9%84%d8%a7%d8%b3%d8%a6%d9%84%d8%a9') === -1) return;
    if (document.querySelector('.cc-faq')) return;
    var ar = ccIsAr();
    var faqs = ar ? [
      { q: 'كم يستغرق التوصيل داخل الكويت؟', a: 'التوصيل داخل الكويت يتم خلال ٢٤ ساعة من تأكيد الطلب. الطلبات بعد الساعة ٢ ظهراً تُشحن في اليوم التالي.' },
      { q: 'هل المنتجات أصلية ١٠٠٪؟', a: 'نعم، جميع منتجاتنا أصلية ومستوردة مباشرة من الشركات المصنعة مع ضمان رسمي.' },
      { q: 'ما هي سياسة الإرجاع والاستبدال؟', a: 'يمكنك إرجاع أو استبدال المنتج خلال ١٤ يوم من تاريخ الاستلام بشرط أن يكون بحالته الأصلية مع العلبة والملحقات.' },
      { q: 'هل تتوفر خدمة الدفع عند الاستلام؟', a: 'نعم، نوفر خدمة الدفع عند الاستلام داخل الكويت بالإضافة إلى الدفع الإلكتروني عبر K-Net وبطاقات الائتمان.' },
      { q: 'هل يوجد ضمان على المنتجات؟', a: 'نعم، جميع الأجهزة تأتي بضمان المصنع الرسمي. مدة الضمان تختلف حسب المنتج والشركة المصنعة.' },
      { q: 'هل تشحنون خارج الكويت؟', a: 'نعم، نوفر الشحن الدولي لدول الخليج والشرق الأوسط. تكلفة ومدة الشحن تختلف حسب الوجهة.' },
      { q: 'كيف أتواصل مع خدمة العملاء؟', a: 'يمكنك متابعتنا على انستقرام @mahsool.kw لآخر أخبارنا وطرق التواصل.' }
    ] : [
      { q: 'How long does delivery take in Kuwait?', a: 'Delivery within Kuwait takes 24 hours from order confirmation. Orders placed after 2 PM ship the next business day.' },
      { q: 'Are all products 100% authentic?', a: 'Yes, all our products are genuine and imported directly from manufacturers with official warranty.' },
      { q: 'What is your return policy?', a: 'You can return or exchange within 14 days of delivery, provided the product is in its original condition with box and accessories.' },
      { q: 'Do you offer Cash on Delivery?', a: 'Yes, we offer COD within Kuwait, along with K-Net and credit card payments.' },
      { q: 'Do products come with warranty?', a: 'Yes, all devices come with official manufacturer warranty. Duration varies by product.' },
      { q: 'Do you ship outside Kuwait?', a: 'Yes, we offer international shipping to GCC and Middle East. Cost and duration vary by destination.' },
      { q: 'How can I contact customer service?', a: 'Follow us on Instagram @mahsool.kw for updates and contact info.' }
    ];
    var main = document.querySelector('main#MainContent .page-width, main .page-width, main');
    if (!main) return;
    var sec = document.createElement('div');
    sec.className = 'cc-faq';
    sec.innerHTML =
      '<h1 class="cc-faq__title">' + (ar ? 'الأسئلة الشائعة' : 'Frequently Asked Questions') + '</h1>' +
      '<div class="cc-faq__list">' +
        faqs.map(function (f, i) {
          return '<div class="cc-faq__item">' +
            '<button class="cc-faq__q" aria-expanded="false" aria-controls="cc-faq-a-' + i + '">' +
              '<span>' + f.q + '</span><span class="cc-faq__arrow">›</span>' +
            '</button>' +
            '<div class="cc-faq__a" id="cc-faq-a-' + i + '"><p>' + f.a + '</p></div>' +
          '</div>';
        }).join('') +
      '</div>';
    var existing = main.querySelector('.rte, .page__content');
    if (existing) existing.parentNode.insertBefore(sec, existing);
    else main.prepend(sec);
    sec.querySelectorAll('.cc-faq__q').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        sec.querySelectorAll('.cc-faq__q[aria-expanded="true"]').forEach(function (b) {
          if (b !== btn) { b.setAttribute('aria-expanded', 'false'); b.parentNode.classList.remove('cc-faq__item--open'); }
        });
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        btn.parentNode.classList.toggle('cc-faq__item--open', !open);
      });
    });
  }

  // -------- 6. Cross-sell "You may also like" --------
  function ccInjectCrossSell() {
    if (!document.body.classList.contains('template-product')) return;
    if (document.querySelector('.cc-cross-sell')) return;
    var canon = document.querySelector('link[rel="canonical"]');
    var url = canon ? canon.href : window.location.href;
    var match = url.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    fetch('/products/' + match[1] + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) { return ccFetchCrossSell(p.id); })
      .catch(function () {});
  }
  function ccFetchCrossSell(productId) {
    if (!productId) return;
    var ar = ccIsAr();
    var root = ccRootSlash();
    var cur = ar ? ' د.ك' : ' KWD';
    fetch('/recommendations/products.json?product_id=' + productId + '&limit=4&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = data.products || [];
        if (!products.length) return;
        var sec = document.createElement('section');
        sec.className = 'cc-cross-sell';
        sec.innerHTML =
          '<div class="cc-cross-sell__inner">' +
            '<h2 class="cc-cross-sell__title">' + (ar ? 'قد يعجبك أيضاً' : 'You may also like') + '</h2>' +
            '<div class="cc-cross-sell__grid">' +
              products.map(function (p) {
                var img = p.featured_image || (p.images && p.images[0]) || '';
                var price = (p.price / 1000).toFixed(3);
                var cmp = p.compare_at_price ? (p.compare_at_price / 1000).toFixed(3) : '';
                return '<a class="cc-cross-sell__card" href="' + root + 'products/' + p.handle + '">' +
                  '<div class="cc-cross-sell__img">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</div>' +
                  '<div class="cc-cross-sell__info">' +
                    '<div class="cc-cross-sell__name">' + p.title + '</div>' +
                    '<div class="cc-cross-sell__price">' + (cmp ? '<s>' + cmp + '</s> ' : '') + price + cur + '</div>' +
                  '</div>' +
                '</a>';
              }).join('') +
            '</div>' +
          '</div>';
        var rv = document.querySelector('.cc-rv');
        var main = document.querySelector('main#MainContent, main');
        if (rv) rv.parentNode.insertBefore(sec, rv);
        else if (main) main.appendChild(sec);
      })
      .catch(function () {});
  }

  // =====================================================================
  // ROUND 63 — Sticky Header, Variant Swatches (CSS-only), Scroll-Top
  //            Enhancement, Announcement Bar, Loading Progress Bar
  // =====================================================================

  // ── 1. Sticky Header ──
  function ccInitStickyHeader() {
    if (document.querySelector('.cc-header-spacer')) return;
    var headerSection = document.querySelector('.shopify-section-group-header-group') ||
                        document.querySelector('.section-header');
    if (!headerSection) return;
    var headerWrapper = headerSection.querySelector('.header-wrapper') ||
                        headerSection.querySelector('sticky-header');
    if (!headerWrapper) return;

    // Create spacer
    var spacer = document.createElement('div');
    spacer.className = 'cc-header-spacer';
    spacer.style.height = '0px';
    headerWrapper.parentElement.insertBefore(spacer, headerWrapper.nextSibling);

    var lastScrollY = 0;
    var stuck = false;
    var bannerEl = document.querySelector('.cc-banner') || document.querySelector('.banner') ||
                   document.querySelector('[class*="slideshow"]');
    var triggerPoint = bannerEl ? bannerEl.offsetHeight : 300;

    function onScroll() {
      var y = window.scrollY;
      if (y > triggerPoint && !stuck) {
        stuck = true;
        document.body.classList.add('cc-header-stuck');
        spacer.style.height = headerWrapper.offsetHeight + 'px';
      } else if (y <= triggerPoint && stuck) {
        stuck = false;
        document.body.classList.remove('cc-header-stuck');
        spacer.style.height = '0px';
      }
      lastScrollY = y;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      triggerPoint = bannerEl ? bannerEl.offsetHeight : 300;
    }, { passive: true });
    onScroll();
  }

  // ── 3. Back to Top — Enhanced (CSS handles animation, JS adds smooth scroll with easing) ──
  // Already handled by existing ccInjectScrollTop + new CSS keyframes

  // ── 4. Announcement Bar ──
  function ccInjectAnnounceBar() {
    if (document.querySelector('.cc-announce-bar')) return;
    var ar = ccIsAr();

    var messages = ar ? [
      { icon: 'truck', text: 'شحن مجاني للطلبات فوق 15 د.ك' },
      { icon: 'shield', text: 'ضمان جودة على جميع المنتجات' },
      { icon: 'gift', text: 'عروض حصرية لعملائنا المميزين' },
      { icon: 'clock', text: 'توصيل سريع خلال 2-3 أيام عمل' }
    ] : [
      { icon: 'truck', text: 'Free Shipping on Orders Over 15 KWD' },
      { icon: 'shield', text: 'Quality Guarantee on All Products' },
      { icon: 'gift', text: 'Exclusive Offers for Our Valued Customers' },
      { icon: 'clock', text: 'Fast Delivery Within 2-3 Business Days' }
    ];

    var icons = {
      truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
      shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
      gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="8" width="18" height="4"/><path d="M12 8v13M3 12h18v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7z"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C9 3 12 8 12 8S15 3 16.5 3a2.5 2.5 0 0 1 0 5"/></svg>',
      clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>'
    };

    // Build items — duplicate for seamless loop
    var itemsHtml = '';
    for (var r = 0; r < 2; r++) {
      for (var i = 0; i < messages.length; i++) {
        itemsHtml += '<span class="cc-announce-bar__item">' + icons[messages[i].icon] + messages[i].text + '</span>';
        if (i < messages.length - 1 || r < 1) {
          itemsHtml += '<span class="cc-announce-bar__dot"></span>';
        }
      }
      if (r === 0) itemsHtml += '<span class="cc-announce-bar__dot"></span>';
    }

    var bar = document.createElement('div');
    bar.className = 'cc-announce-bar';
    bar.innerHTML = '<div class="cc-announce-bar__track">' + itemsHtml + '</div>';

    // Insert at very top of body, before header
    var headerGroup = document.querySelector('.shopify-section-group-header-group') ||
                      document.querySelector('.section-header') ||
                      document.body.firstElementChild;
    if (headerGroup && headerGroup.parentElement) {
      headerGroup.parentElement.insertBefore(bar, headerGroup);
    } else {
      document.body.insertBefore(bar, document.body.firstElementChild);
    }
  }

  // ── 5. Loading Progress Bar ──
  function ccInitProgressBar() {
    if (document.querySelector('.cc-progress-bar')) return;
    var bar = document.createElement('div');
    bar.className = 'cc-progress-bar';
    document.body.appendChild(bar);

    // Simulate loading progress
    var progress = 10;
    bar.style.width = progress + '%';

    var interval = setInterval(function () {
      if (progress < 85) {
        progress += Math.random() * 12;
        bar.style.width = Math.min(progress, 85) + '%';
      }
    }, 150);

    function finish() {
      clearInterval(interval);
      bar.style.width = '100%';
      bar.classList.add('cc-progress-bar--done');
      setTimeout(function () {
        if (bar.parentElement) bar.parentElement.removeChild(bar);
      }, 800);
    }

    if (document.readyState === 'complete') {
      finish();
    } else {
      window.addEventListener('load', finish);
    }
  }

  // =====================================================================
  // ROUND 64 — Mega Menu, Product Tabs, Page Transitions, JSON-LD,
  //            Lazy Load, Parallax Banner, Collection Hero
  // =====================================================================

  // ── 1. Mega Menu — Inject collection images into drawer menu items ──
  function ccEnhanceMegaMenu() {
    var items = document.querySelectorAll('.menu-drawer__menu.has-submenu > li > details > .menu-drawer__submenu .menu-drawer__menu > li > a.menu-drawer__menu-item');
    if (!items.length) return;
    items.forEach(function (a) {
      if (a.querySelector('.cc-mega-img') || a.classList.contains('cc-mega-done')) return;
      a.classList.add('cc-mega-done', 'cc-mega-item');
      var href = a.getAttribute('href');
      if (!href || href.indexOf('/collections/') === -1) return;
      var handle = href.split('/collections/')[1];
      if (!handle) return;
      handle = handle.split('?')[0].split('#')[0];
      // Fetch collection image
      fetch('/collections/' + handle + '.json')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.collection && data.collection.image && data.collection.image.src) {
            var img = document.createElement('img');
            img.className = 'cc-mega-img';
            img.src = data.collection.image.src.replace(/(\.[a-z]+)\?/, '_120x120$1?');
            img.alt = data.collection.title || '';
            img.loading = 'lazy';
            a.insertBefore(img, a.firstChild);
          }
        })
        .catch(function () {});
    });
  }

  // ── 2. Product Tabs ──
  function ccInjectProductTabs() {
    if (!document.body.classList.contains('template-product')) return;
    if (document.querySelector('.cc-product-tabs')) return;
    var descEl = document.querySelector('.product__description');
    if (!descEl) return;

    var ar = ccIsAr();
    var descHTML = descEl.innerHTML;

    // Build tabs data
    var tabs = [
      { id: 'desc', label: ar ? 'الوصف' : 'Description', content: descHTML },
      { id: 'specs', label: ar ? 'المواصفات' : 'Specifications', content: '' },
      { id: 'shipping', label: ar ? 'الشحن والإرجاع' : 'Shipping & Returns', content: '' }
    ];

    // Try to extract specs from description (lines with ":" pattern)
    var tempDiv = document.createElement('div');
    tempDiv.innerHTML = descHTML;
    var textContent = tempDiv.textContent || '';
    var specLines = textContent.split('\n').filter(function (l) { return l.indexOf(':') > 0 && l.trim().length > 5; });
    if (specLines.length >= 2) {
      tabs[1].content = '<ul>' + specLines.map(function (l) {
        var parts = l.split(':');
        return '<li><strong>' + parts[0].trim() + ':</strong> ' + parts.slice(1).join(':').trim() + '</li>';
      }).join('') + '</ul>';
    } else {
      tabs[1].content = ar
        ? '<p>للمزيد من المعلومات حول مواصفات هذا المنتج، تابعونا على انستقرام @mahsool.kw.</p>'
        : '<p>For more details about this product\'s specifications, follow us on Instagram @mahsool.kw.</p>';
    }

    // Shipping tab
    tabs[2].content = ar
      ? '<ul><li>التوصيل داخل الكويت: 2-3 أيام عمل</li><li>الشحن الدولي: 5-10 أيام عمل</li><li>شحن مجاني للطلبات فوق 15 د.ك</li><li>سياسة الإرجاع: 14 يوم من تاريخ الاستلام</li><li>المنتج يجب أن يكون بحالته الأصلية مع التغليف</li></ul>'
      : '<ul><li>Delivery within Kuwait: 2-3 business days</li><li>International shipping: 5-10 business days</li><li>Free shipping on orders over 15 KWD</li><li>Return policy: 14 days from delivery date</li><li>Product must be in original condition with packaging</li></ul>';

    // Build HTML
    var navHtml = '';
    var panelsHtml = '';
    tabs.forEach(function (tab, i) {
      navHtml += '<button type="button" class="cc-product-tabs__btn' + (i === 0 ? ' cc-product-tabs__btn--active' : '') + '" data-tab="' + tab.id + '">' + tab.label + '</button>';
      panelsHtml += '<div class="cc-product-tabs__panel' + (i === 0 ? ' cc-product-tabs__panel--active' : '') + '" data-panel="' + tab.id + '">' + tab.content + '</div>';
    });

    var wrapper = document.createElement('div');
    wrapper.className = 'cc-product-tabs';
    wrapper.innerHTML = '<div class="cc-product-tabs__nav">' + navHtml + '</div>' + panelsHtml;

    // Insert after description
    descEl.style.display = 'none';
    descEl.parentElement.insertBefore(wrapper, descEl.nextSibling);

    // Tab switching
    wrapper.addEventListener('click', function (e) {
      var btn = e.target.closest('.cc-product-tabs__btn');
      if (!btn) return;
      var tabId = btn.getAttribute('data-tab');
      wrapper.querySelectorAll('.cc-product-tabs__btn').forEach(function (b) { b.classList.remove('cc-product-tabs__btn--active'); });
      wrapper.querySelectorAll('.cc-product-tabs__panel').forEach(function (p) { p.classList.remove('cc-product-tabs__panel--active'); });
      btn.classList.add('cc-product-tabs__btn--active');
      var panel = wrapper.querySelector('[data-panel="' + tabId + '"]');
      if (panel) panel.classList.add('cc-product-tabs__panel--active');
    });
  }

  // ── 3. Animated Page Transitions ──
  function ccInitPageTransitions() {
    if (document.querySelector('.cc-page-transition')) return;
    // Add fade-in class on load
    document.body.classList.add('cc-page-enter');

    // Create overlay
    var overlay = document.createElement('div');
    overlay.className = 'cc-page-transition';
    document.body.appendChild(overlay);

    // Intercept link clicks for smooth transitions
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href');
      // Only intercept internal navigation links
      if (!href || href.charAt(0) === '#' || href.indexOf('javascript:') === 0) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      if (href.indexOf(window.location.origin) !== 0 && href.charAt(0) !== '/') return;
      // Skip AJAX/cart links
      if (href.indexOf('/cart') === 0 && href.indexOf('/cart/') === -1) return;
      if (link.closest('.menu-drawer') || link.closest('.cart-drawer')) return;

      e.preventDefault();
      overlay.classList.add('cc-page-transition--active');
      setTimeout(function () {
        window.location.href = href;
      }, 280);
    });
  }

  // ── 4. Structured Data JSON-LD ──
  function ccInjectJsonLd() {
    // Organization schema (all pages)
    if (!document.querySelector('script[data-cc-jsonld="org"]')) {
      var orgSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Mahsool',
        url: window.location.origin,
        logo: window.location.origin + '/cdn/shop/files/mahsool-logo-text.png',
        description: 'Premium coffee equipment store in Kuwait',
        address: { '@type': 'PostalAddress', addressCountry: 'KW' },
        sameAs: []
      };
      var s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-cc-jsonld', 'org');
      s.textContent = JSON.stringify(orgSchema);
      document.head.appendChild(s);
    }

    // Product schema (product pages)
    if (document.body.classList.contains('template-product') && !document.querySelector('script[data-cc-jsonld="product"]')) {
      var prodMeta = document.querySelector('[data-product-json]');
      if (prodMeta) {
        try {
          var prodData = JSON.parse(prodMeta.textContent);
          var variant = prodData.variants && prodData.variants[0];
          var productSchema = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: prodData.title,
            description: (prodData.description || '').replace(/<[^>]*>/g, '').substring(0, 500),
            image: prodData.featured_image,
            brand: { '@type': 'Brand', name: prodData.vendor },
            offers: {
              '@type': 'Offer',
              url: window.location.href,
              priceCurrency: 'KWD',
              price: variant ? (variant.price / 1000).toFixed(3) : '0',
              availability: prodData.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
            }
          };
          if (prodData.id) productSchema.sku = String(prodData.id);
          var ps = document.createElement('script');
          ps.type = 'application/ld+json';
          ps.setAttribute('data-cc-jsonld', 'product');
          ps.textContent = JSON.stringify(productSchema);
          document.head.appendChild(ps);
        } catch (_) {}
      }
    }

    // BreadcrumbList schema
    var breadcrumbs = document.querySelectorAll('.cc-breadcrumb a, .breadcrumb a');
    if (breadcrumbs.length > 0 && !document.querySelector('script[data-cc-jsonld="breadcrumb"]')) {
      var items = [];
      breadcrumbs.forEach(function (a, i) {
        items.push({
          '@type': 'ListItem',
          position: i + 1,
          name: a.textContent.trim(),
          item: window.location.origin + a.getAttribute('href')
        });
      });
      var bcSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items
      };
      var bs = document.createElement('script');
      bs.type = 'application/ld+json';
      bs.setAttribute('data-cc-jsonld', 'breadcrumb');
      bs.textContent = JSON.stringify(bcSchema);
      document.head.appendChild(bs);
    }
  }

  // ── 5. Lazy Load Images ──
  function ccInitLazyLoad() {
    if (typeof IntersectionObserver === 'undefined') return;
    // Find images that don't have loading="lazy" and are below the fold
    var images = document.querySelectorAll('img[data-src]:not(.cc-lazy--loaded), img[loading="lazy"]:not(.cc-lazy--loaded)');
    if (!images.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          }
          img.classList.add('cc-lazy--loaded');
          observer.unobserve(img);
        }
      });
    }, { rootMargin: '200px' });

    images.forEach(function (img) {
      img.classList.add('cc-lazy');
      observer.observe(img);
      // Trigger loaded class when image loads naturally
      if (img.complete) {
        img.classList.add('cc-lazy--loaded');
      } else {
        img.addEventListener('load', function () {
          img.classList.add('cc-lazy--loaded');
        });
      }
    });
  }

  // ── 6. Parallax Banner ──
  function ccInitParallax() {
    var banner = document.querySelector('.cc-banner') || document.querySelector('.banner');
    if (!banner) return;
    if (banner.classList.contains('cc-parallax-done')) return;
    banner.classList.add('cc-parallax-done', 'cc-parallax-banner');

    var media = banner.querySelector('img, video, .banner__media');
    if (!media) return;

    function onScroll() {
      var scrollY = window.scrollY;
      var bannerH = banner.offsetHeight;
      if (scrollY > bannerH + 100) return; // Skip when past banner
      var offset = scrollY * 0.35;
      media.style.transform = 'translateY(' + offset + 'px) scale(1.08)';
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // Initial scale
    media.style.transform = 'scale(1.08)';
    media.style.willChange = 'transform';
  }

  // ── 7. Collection Page Hero Enhancement ──
  function ccEnhanceCollectionHero() {
    if (!document.body.classList.contains('template-collection')) return;
    var hero = document.querySelector('.collection-hero');
    if (!hero || hero.classList.contains('cc-hero-done')) return;
    hero.classList.add('cc-hero-done');

    // Add product count badge
    var ar = ccIsAr();
    var countEl = document.querySelector('.collection-product-count') || document.querySelector('[id*="ProductCount"]');
    var countText = '';
    if (countEl) {
      var match = countEl.textContent.match(/\d+/);
      if (match) {
        countText = ar ? match[0] + ' منتج' : match[0] + ' Products';
      }
    }
    if (countText) {
      var badge = document.createElement('div');
      badge.className = 'cc-collection-count';
      badge.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>' + countText;
      var titleEl = hero.querySelector('.collection-hero__title');
      if (titleEl) titleEl.parentElement.insertBefore(badge, titleEl.nextSibling);
    }
  }

  // ── Round 64 Orchestrator ──
  function ccRunAllRound64() {
    try { ccInjectProductTabs(); } catch (_) {}
    try { ccInitPageTransitions(); } catch (_) {}
    try { ccInjectJsonLd(); } catch (_) {}
    try { ccInitLazyLoad(); } catch (_) {}
    try { ccInitParallax(); } catch (_) {}
    try { ccEnhanceCollectionHero(); } catch (_) {}
    // Mega menu enhancement runs after a delay to let drawer DOM load
    setTimeout(function () { try { ccEnhanceMegaMenu(); } catch (_) {} }, 500);
  }

  // ── Round 63 Orchestrator ──
  function ccRunAllRound63() {
    try { ccInitProgressBar(); } catch (_) {}
    try { ccInjectAnnounceBar(); } catch (_) {}
    try { ccInitStickyHeader(); } catch (_) {}
  }

  function ccRunAllRound61() {
    try { ccInjectToast(); } catch (_) {}
    try { ccInjectProductBadges(); } catch (_) {}
    try { ccInjectLowStock(); } catch (_) {}
    try { ccInjectQuickViewButtons(); } catch (_) {}
    try { ccRenderFaqPage(); } catch (_) {}
    try { ccInjectCrossSell(); } catch (_) {}
  }

  function ccRunAllRound60() {
    try { ccInjectStickyAtc(); } catch (_) {}
    try { ccInjectShipProgress(); } catch (_) {}
    try { ccTrackRecentlyViewed(); } catch (_) {}
    try { ccRenderRecentlyViewed(); } catch (_) {}
    try { ccInjectTrustBadges(); } catch (_) {}
    try { ccInjectBreadcrumbs(); } catch (_) {}
    try { ccEnhanceEmptyStates(); } catch (_) {}
    try { ccInjectScrollTop(); } catch (_) {}
    try { ccInjectFilterChips(); } catch (_) {}
    try { ccInjectImageZoom(); } catch (_) {}
  }

  function ccRunAllRound57() {
    try { ccInjectWishlistHearts(); } catch (_) {}
    try { ccInjectHeaderWishlist(); } catch (_) {}
    try { ccRenderWishlistPage(); } catch (_) {}
    try { ccEnhanceSearch(); } catch (_) {}
    try { ccInjectReviewStars(); } catch (_) {}
    try { ccInjectBackInStock(); } catch (_) {}
    try { ccInjectSocialBar(); } catch (_) {}
    try { ccRenderAboutPage(); } catch (_) {}
    try { ccRenderTrackingPage(); } catch (_) {}
    try { ccRenderShippingPage(); } catch (_) {}
    try { ccPolishNewsletter(); } catch (_) {}
    try { ccUpdateWLBadge(); } catch (_) {}
  }

  function runAll() {
    init();
    wireBannerHomeLink();
    injectBrandsCTA();
    injectPageBanner();
    removeEmptyFooterBlocks();
    injectFooterLangBlock();
    injectHomePagination();
    injectAllProductsMenuItem();
    injectQuickAdd();
    injectVariantCartButtons();
    makeCollectionCardsClickable();
    applyBilingualLabels();
    ccRunAllRound57();
    ccRunAllRound60();
    ccRunAllRound61();
    ccRunAllRound63();
    ccRunAllRound64();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    runAll();
  }

  // Re-run after Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.classList) return;
    if (e.target.classList.contains('section-header')) {
      setTimeout(init, 50);
      setTimeout(injectAllProductsMenuItem, 80);
    }
    // Re-wire banner link if the banner section was reloaded
    setTimeout(wireBannerHomeLink, 50);
    // Re-inject rectangular cart buttons for any reloaded product grids
    setTimeout(injectQuickAdd, 50);
    setTimeout(injectVariantCartButtons, 50);
  });

  // Watch for dynamically added product/collection cards
  if (typeof MutationObserver !== 'undefined') {
    let moTimer = null;
    const mo = new MutationObserver(function () {
      if (moTimer) clearTimeout(moTimer);
      moTimer = setTimeout(function () {
        injectAllProductsMenuItem();
        injectQuickAdd();
        injectVariantCartButtons();
        makeCollectionCardsClickable();
        applyBilingualLabels();
        ccInjectWishlistHearts();
        ccInjectHeaderWishlist();
        ccUpdateWLBadge();
        try { ccInjectStickyAtc(); } catch (_) {}
        try { ccInjectShipProgress(); } catch (_) {}
        try { ccInjectBreadcrumbs(); } catch (_) {}
        try { ccInjectTrustBadges(); } catch (_) {}
        try { ccInjectImageZoom(); } catch (_) {}
        try { ccInjectFilterChips(); } catch (_) {}
        try { ccInjectProductBadges(); } catch (_) {}
        try { ccInjectQuickViewButtons(); } catch (_) {}
      }, 120);
    });
    if (document.body) {
      mo.observe(document.body, { childList: true, subtree: true });
    } else {
      document.addEventListener('DOMContentLoaded', function () {
        mo.observe(document.body, { childList: true, subtree: true });
      });
    }
  }
})();
